import type { ErrorCode } from '@/api/generated/schemas.ts';

const de = {
    app: {
        name: 'Recap',
        expandSidebar: 'Seitenleiste ausklappen',
        collapseSidebar: 'Seitenleiste einklappen',
    },
    nav: {
        home: 'Übersicht',
        activities: 'Aktivitäten',
        projects: 'Projekte',
        clients: 'Kunden',
        recaps: 'Recaps',
        sources: 'Quellen',
        favorites: 'Favoriten',
        workspace: 'Workspace',
        connections: 'Verbindungen',
        team: 'Team',
        settings: 'Einstellungen',
        userSettings: 'Kontoeinstellungen',
    },
    rail: {
        dueTodos_one: '{{count}} To-do fällig',
        dueTodos_other: '{{count}} To-dos fällig',
        drafts_one: '{{count}} Entwurf',
        drafts_other: '{{count}} Entwürfe',
        unfiledSources_one: '{{count}} Quelle in keinem Recap',
        unfiledSources_other: '{{count}} Quellen in keinem Recap',
        acknowledgements_one: '{{count}} wartet auf deine Bestätigung',
        acknowledgements_other: '{{count}} warten auf deine Bestätigung',
        new: 'Neu seit deinem letzten Besuch',
        failingFeeds: 'Eine Verbindung funktioniert nicht',
    },
    auth: {
        preview: {
            title: 'Kick-off Website-Relaunch',
            summary:
                'Petra Müller und Lena Weber haben besprochen, wie der Online-Shop der Bäckerei Müller startet: erst mit Brot und Gebäck, Torten folgen im Frühjahr. Die Fotos entstehen im März in der Backstube.',
        },
        oauth: {
            title: '{{client}} mit Recap verbinden',
            description:
                '{{client}} kann dann in deinem Namen und mit deinen Rechten Recaps finden und lesen, Quellen hinzufügen, Recaps erstellen, bearbeiten und veröffentlichen, To-dos abhaken und kommentieren.',
            workspace: 'Workspace',
            readOnly: 'Nur lesen',
            readOnlyHint: '{{client}} kann dann nichts ändern.',
            afterwards:
                'Danach wirst du zu {{host}} zurückgeleitet. Die Verbindung erscheint in {{workspace}} unter „Meine Verbindungen“. Dort kannst du sie jederzeit trennen.',
            allow: 'Erlauben',
            deny: 'Ablehnen',
            invalidTitle: 'Diese Anfrage ist ungültig',
            noWorkspaceTitle: 'Kein Workspace',
            noWorkspace: 'Du bist noch in keinem Workspace Mitglied, auf den eine Anwendung für dich zugreifen könnte.',
        },
        account: 'Konto ({{name}})',
        back: 'Zurück',
        step: 'Schritt {{step}} von {{count}}',
        signOut: 'Abmelden',
        fields: {
            email: 'E-Mail-Adresse',
        },
        signIn: {
            title: 'Bei Recap anmelden',
            description: 'Melde dich mit Google oder deiner E-Mail-Adresse an. Ein Passwort brauchst du nicht.',
            descriptionWithoutGoogle: 'Melde dich mit deiner E-Mail-Adresse an. Ein Passwort brauchst du nicht.',
            invitedDescription: 'Melde dich an, um {{name}} beizutreten. Ein Passwort brauchst du nicht.',
            submit: 'Anmelden',
            withEmail: 'Mit E-Mail anmelden',
            emailHint: 'Wir schicken dir einen Link zum Anmelden.',
            newAccount: 'Noch kein Konto? Beim ersten Anmelden erstellst du eins.',
            terms: 'Für Recap gelten die <terms>AGB</terms> und die <privacy>Datenschutzerklärung</privacy>.',
        },
        verify: {
            title: 'Bestätige deine E-Mail-Adresse',
            description: 'Wir haben dir einen Code an {{email}} geschickt.',
            code: 'Code',
            codeInvalid: 'Gib den 6-stelligen Code ein.',
            submit: 'Bestätigen',
            sendAgain: 'Code erneut senden',
            sent: 'Wir haben dir einen neuen Code an {{email}} geschickt.',
            changeAddress: 'Andere Adresse verwenden',
            changeTitle: 'Andere Adresse verwenden',
            changeDescription:
                'Wir schicken einen Code an die neue Adresse. Sobald du ihn eingibst, ersetzt sie deine bisherige.',
            newEmail: 'Neue E-Mail-Adresse',
            sendCode: 'Code senden',
        },
        code: {
            title: 'Schau in dein Postfach',
            description:
                'Gib den Code ein, den wir an {{email}} geschickt haben, oder klicke in der E-Mail auf „Anmelden“.',
            label: 'Code',
            digit: 'Ziffer {{number}}',
            hint: 'Sobald alle sechs Ziffern da sind, melden wir dich an.',
            openInbox: '{{name}} öffnen',
            notReceived: 'Kein Code angekommen?',
            sendAgain: 'Code erneut senden',
            sent: 'Wir haben dir einen neuen Code geschickt.',
        },
        link: {
            title: 'Wir melden dich an …',
            failedTitle: 'Dieser Link funktioniert nicht mehr',
            failedDescription: 'Jeder Link funktioniert nur einmal und 15 Minuten lang.',
            requestCode: 'Neuen Code anfordern',
        },
        sso: {
            signInWithGoogle: 'Mit Google anmelden',
            lastUsed: 'Zuletzt verwendet',
            or: 'oder',
        },
        setup: {
            continue: 'Weiter',
            create: 'Workspace erstellen',
            join: '{{name}} beitreten',
            profile: {
                title: 'Wie heißt du?',
                description: 'Mehr brauchen wir fürs Erste nicht.',
            },
            workspace: {
                title: 'Richte deinen Workspace ein',
                description: 'So erscheint dein Workspace in der App.',
            },
            joining: {
                title: 'Willkommen, {{name}}',
                description: 'Tritt einem Workspace bei oder erstelle einen neuen.',
                member: 'Du bist als Mitglied eingeladen.',
                employee: 'Du bist eingeladen, die Recaps für {{client}} zu lesen.',
                join: 'Beitreten',
                create: 'Neuen Workspace erstellen',
            },
            waitlist: {
                title: 'Du stehst auf der Warteliste',
                description:
                    'Recap ist gerade nur auf Einladung nutzbar. Sobald dein Zugang freigeschaltet ist, schicken wir dir eine E-Mail an {{email}}.',
                invited: 'Wurdest du in einen Workspace eingeladen? Öffne den Link aus der Einladung.',
            },
        },
        invitation: {
            title: '{{name}} beitreten',
            descriptionSignedOut: 'Die Einladung gilt für {{email}}. Melde dich mit dieser Adresse an, um beizutreten.',
            descriptionSignedIn: 'Du bist als {{email}} angemeldet.',
            descriptionLink: 'Melde dich an, um beizutreten.',
            descriptionOtherEmail:
                'Die Einladung gilt für {{email}}, du bist aber als {{currentEmail}} angemeldet. Melde dich ab und mit {{email}} wieder an.',
            join: 'Beitreten',
            signIn: 'Anmelden',
            invalidTitle: 'Dieser Einladungslink ist nicht mehr gültig',
            invalidDescription:
                'Er wurde schon verwendet oder zurückgezogen. Bitte die Inhaberin oder den Inhaber des Workspaces um einen neuen Link.',
        },
        employeeInvitation: {
            title: 'Recaps für {{client}}',
            invitedBy: '{{workspace}} lädt dich ein, die Recaps für {{client}} zu lesen.',
            descriptionLink: 'Melde dich an, um beizutreten.',
            invalidDescription:
                'Er wurde schon verwendet, zurückgezogen oder durch einen neuen ersetzt. Bitte um einen neuen Link.',
        },
    },
    common: {
        toRecaps: 'Zu den Recaps',
        audio: {
            play: 'Abspielen',
            loading: 'Wird geladen …',
            failed: 'Die Aufnahme konnte nicht geladen werden.',
        },
        users: {
            deleted: 'Gelöschtes Konto',
            deletedBot: 'Gelöschter Bot',
            disabled: '{{name}} (deaktiviert)',
        },
        dates: {
            today: 'Heute',
            tomorrow: 'Morgen',
            yesterday: 'Gestern',
            inSentence: {
                today: 'heute',
                tomorrow: 'morgen',
                yesterday: 'gestern',
            },
            thisWeek: 'Diese Woche',
        },
        showPassword: 'Passwort anzeigen',
        hidePassword: 'Passwort verbergen',
        cancel: 'Abbrechen',
        back: 'Zurück',
        close: 'Schließen',
        copy: 'Kopieren',
        copied: 'Kopiert',
        clear: 'Leeren',
        selected_zero: 'Nichts ausgewählt',
        selected_one: '{{count}} ausgewählt',
        selected_other: '{{count}} ausgewählt',
        clearSelection: 'Auswahl aufheben',
        showAll: 'Alle anzeigen',
        showLess: 'Weniger anzeigen',
        dangerZone: 'Kritische Aktionen',
        moreActions: 'Weitere Aktionen',
        optional: 'optional',
        pageNotFound: 'Seite nicht gefunden',
        somethingWentWrong: 'Etwas ist schiefgelaufen',
        backToDashboard: 'Zur Übersicht',
        pagination: 'Seiten',
        previousPage: 'Vorherige Seite',
        nextPage: 'Nächste Seite',
        itemRange: '{{first}}–{{last}} von {{count}}',
        sync: 'Abrufen',
        removeItem: '{{item}} entfernen',
        syncing: 'Wird abgerufen …',
        save: 'Speichern',
        create: 'Erstellen',
        edit: 'Bearbeiten',
        autoSave: 'Speichert automatisch',
        autoSaving: 'Wird gespeichert …',
        autoSaved: 'Gespeichert',
        autoSaveFailed: 'Nicht gespeichert',
        autoSaveRetry: 'Noch einmal versuchen',
        advanced: 'Erweitert',
        resetFilters: 'Filter zurücksetzen',
        yes: 'Ja',
        no: 'Nein',
    },
    viewer: {
        page: 'Seite {{current}} von {{count}}',
        zoomIn: 'Vergrößern',
        zoomOut: 'Verkleinern',
        fit: 'An Fenster anpassen',
        loading: 'Wird geladen …',
        loadError: 'Die Datei konnte nicht geladen werden.',
        noPreview: 'Für diese Datei gibt es keine Vorschau.',
    },
    imageField: {
        remove: 'Entfernen',
    },
    dropzone: {
        chooseFiles: 'Dateien auswählen',
        takePhoto: 'Foto aufnehmen',
    },
    avatarColor: {
        pick: 'Farbe auswählen',
        none: 'Keine Farbe',
        colors: {
            red: 'Rot',
            orange: 'Orange',
            amber: 'Bernstein',
            yellow: 'Gelb',
            lime: 'Limette',
            green: 'Grün',
            emerald: 'Smaragd',
            teal: 'Petrol',
            cyan: 'Cyan',
            sky: 'Himmelblau',
            blue: 'Blau',
            indigo: 'Indigo',
            violet: 'Violett',
            purple: 'Lila',
            fuchsia: 'Fuchsia',
            pink: 'Pink',
            rose: 'Rosé',
        },
    },
    errors: {
        failed: 'Das hat nicht geklappt. Versuche es noch einmal.',
        // One per ErrorCode the API answers with.
        api: {
            not_signed_in: 'Du wurdest abgemeldet. Melde dich erneut an.',
            csrf_failed: 'Die Seite war zu lange geöffnet. Lade sie neu.',
            not_found: 'Das wurde nicht gefunden. Vielleicht wurde es inzwischen gelöscht.',
            owner_only: 'Das kann nur die Inhaberin oder der Inhaber des Workspaces.',
            required: 'Pflichtfeld',
            too_long: 'Zu lang',
            invalid: 'Ungültiger Wert',
            invalid_reference: 'Das gehört zu einem anderen Workspace. Lade die Seite neu.',
            email_taken: 'Diese E-Mail-Adresse wird schon verwendet.',
            too_many_attempts: 'Zu viele Versuche. Warte ein paar Minuten und versuche es dann noch einmal.',
            email_not_sent: 'Die E-Mail konnte nicht gesendet werden. Versuche es gleich noch einmal.',
            email_not_verified: 'Bestätige zuerst deine E-Mail-Adresse.',
            approval_pending:
                'Dein Zugang ist noch nicht freigeschaltet. Wir schicken dir eine E-Mail, sobald es so weit ist.',
            already_approved: 'Dein Zugang ist schon freigeschaltet.',
            wrong_code: 'Der Code ist falsch.',
            code_expired: 'Der Code ist abgelaufen. Fordere einen neuen an.',
            invitation_for_other_email: 'Die Einladung gilt für eine andere E-Mail-Adresse.',
            slug_taken: 'Dieses Kürzel ist schon vergeben.',
            terms_not_accepted:
                'Akzeptiere die AGB und den Auftragsverarbeitungsvertrag, um den Workspace zu erstellen.',
            owner_cannot_leave:
                'Als Inhaber:in kannst du den Workspace nicht verlassen. Übertrage ihn zuerst an eine andere Person.',
            access_disabled: 'Dein Zugang zu diesem Workspace wurde deaktiviert.',
            owner_cannot_be_disabled: 'Inhaber:innen können nicht deaktiviert werden.',
            owner_cannot_delete_account: 'Übertrage oder lösche zuerst die Workspaces, die dir gehören.',
            email_incorrect: 'Diese E-Mail-Adresse gehört nicht zu deinem Konto.',
            sso_denied:
                'Du hast die Anmeldung bei Google abgebrochen. Versuche es noch einmal oder melde dich mit deiner E-Mail-Adresse an.',
            sso_expired: 'Die Anmeldung bei Google hat zu lange gedauert. Versuche es noch einmal.',
            sso_email_not_verified:
                'Google hat deine E-Mail-Adresse noch nicht bestätigt. Bestätige sie bei Google und versuche es dann noch einmal.',
            sso_unavailable:
                'Google ist gerade nicht erreichbar. Versuche es später noch einmal oder melde dich mit deiner E-Mail-Adresse an.',
            already_member: 'Diese Adresse gehört schon zu einem Mitglied.',
            already_invited: 'Diese Adresse wurde schon eingeladen.',
            already_employee: 'Diese Adresse gehört schon zu den Personen eines Kunden in diesem Workspace.',
            route_overlaps: 'Eine andere Regel dieses Feeds trifft schon auf dieselben Einträge zu.',
            route_query_invalid:
                'Die Suchanfrage lässt sich nicht lesen. Die Markierung im Feld zeigt, was zu ändern ist.',
            feed_taken: 'Ein anderer Feed dieser Verbindung liest das schon.',
            todo_output_not_reported:
                'Die To-dos dieses Projekts gehen an kein Tool, das eine Aufgabe zurückmelden kann. Das geht nur mit einem Webhook.',
            todos_output_taken:
                'Dieses Projekt sendet seine To-dos schon an ein anderes Tool. Entferne dort zuerst „To-dos senden“, um sie hierhin zu senden.',
            feed_lists_in_use: 'Eine Regel dieses Feeds verwendet eine Liste, die dann nicht mehr gelesen würde.',
            image_type_not_allowed: 'Verwende ein Bild im Format PNG, JPEG oder WebP.',
            image_not_readable: 'Das Bild lässt sich nicht öffnen.',
            file_type_not_allowed:
                'Du kannst PDFs, Bilder, E-Mails, Aufnahmen, Office-Dokumente und Textdateien hochladen.',
            file_too_large:
                'Die Datei ist zu groß. Aufnahmen dürfen bis zu 100 MB groß sein, andere Dateien bis zu 20 MB.',
            connection_login_failed: 'Die Anmeldung wurde abgelehnt. Prüfe Benutzername und Passwort.',
            connection_unreachable: 'Der Server ist nicht erreichbar. Prüfe Server und Port.',
            connection_host_not_allowed: 'Dieser Server ist aus dem Internet nicht erreichbar.',
            connection_unsupported_version: 'Diese Odoo-Version ist zu alt. Recap liest Odoo ab Version 17.',
            connection_api_not_included:
                'Dein Tarif bei Odoo.com hat keine externe API. Recap kann sich nur mit dem Tarif „Custom“ verbinden.',
            feed_not_found:
                'Das gibt es dort nicht, oder die Verbindung darf es nicht lesen. Zum Beispiel gibt es den Ordner im Postfach nicht, oder die Notion-Seite ist noch nicht mit der Integration geteilt („•••“ › Verbindungen).',
            feed_sync_running: 'Dieser Feed wird gerade abgerufen.',
            input_on_own_connection:
                'Deine persönlichen Verbindungen können nur senden. Was hereinkommt, gehört dem Workspace.',
            connection_scope_missing:
                'Dafür braucht die Verbindung deine Erlaubnis. Melde dich erneut an, um sie zu erteilen.',
            connection_signed_out: 'Die Verbindung wartet darauf, dass sich jemand erneut anmeldet.',
            oauth_denied: 'Die Anmeldung wurde abgebrochen. Es wurde nichts verbunden.',
            oauth_admin_consent_required:
                'Dein Microsoft-Konto gehört zu einer Organisation, deren Admin Recap erst erlauben muss. Schick deinem Admin den Link zur Freigabe.',
            oauth_other_account: 'Du hast dich mit einem anderen Konto angemeldet als dem, das die Verbindung nutzt.',
            oauth_expired: 'Die Anmeldung hat zu lange gedauert. Versuche es noch einmal.',
            github_installation_unknown:
                'Dein GitHub-Konto hat keinen Zugriff auf diese Installation von Recap. Melde dich bei GitHub mit einem Konto an, das zur Organisation gehört.',
            github_install_requested:
                'Die Organisation muss Recap erst freigeben. Deine Anfrage dazu ist bei GitHub eingegangen. Sobald sie angenommen ist, verbinde GitHub noch einmal.',
            webhook_rejected: 'Die Signatur des Webhooks ist ungültig.',
            webhook_payload_invalid: 'Die Daten, die an den Webhook geschickt wurden, ergeben keine gültige Quelle.',
            token_not_allowed: 'Das geht nicht mit einem API-Schlüssel. Melde dich dafür in Recap an.',
            token_read_only: 'Mit diesem API-Schlüssel kannst du nur lesen.',
            bot_taken: 'Eine andere Verbindung nutzt diesen Bot schon.',
            oauth_request_invalid: 'Die Anfrage kommt nicht von einer Anwendung, der Recap vertrauen kann.',
            recap_processing: 'Der Recap wird gerade verarbeitet.',
            recap_not_publishable:
                'Der Recap kann gerade nicht veröffentlicht werden: Er hat keinen Entwurf oder keinen Titel, oder er wird gerade verarbeitet.',
            recap_not_shareable: 'Nur veröffentlichte Recaps können geteilt werden.',
            recap_draft_not_discardable:
                'Es gibt keinen Entwurf, den du verwerfen könntest, oder der Recap wird gerade verarbeitet.',
            recap_version_not_restorable:
                'Diese Version kann gerade nicht wiederhergestellt werden, oder der Recap wird gerade verarbeitet.',
            source_processing: 'Die Quelle wird gerade verarbeitet.',
            source_has_no_update: 'Für diese Quelle gibt es keine Aktualisierung.',
            source_not_found_at_provider:
                'Keine deiner Verbindungen kann darauf zugreifen. Wurde es gelöscht oder nicht für Recap freigegeben?',
            link_connection_missing:
                'Um diesen Link zu lesen, braucht der Workspace eine Verbindung, die es noch nicht gibt. Verbinde den Dienst in den Einstellungen.',
            source_file_unreadable:
                'Das lässt sich nicht lesen. Vielleicht ist es ein Ordner oder ein Formular, zu groß oder ein Dateityp, den Recap nicht lesen kann.',
            link_without_text:
                'Diese Seite hat keinen eigenen Text, nur Unterseiten. Füge den Link einer Unterseite ein oder lege einen Feed an, der die Unterseiten liest.',
            link_unreadable:
                'Der Link konnte nicht gelesen werden. Die Seite ist vielleicht nicht öffentlich, antwortet nicht, ist zu groß oder enthält keinen Text.',
            client_has_recaps: 'Die Projekte dieses Kunden enthalten Recaps.',
            external_company_linked: 'Diese Firma ist schon mit einem anderen Kunden verknüpft.',
            project_has_recaps: 'Dieses Projekt enthält Recaps.',
            project_without_client:
                'Dieses Projekt ist für keinen Kunden freigegeben. Gib es zuerst im Reiter „Kunde“ für einen frei.',
            project_not_of_client: 'Das Projekt gehört nicht zum Kunden der Quelle.',
            not_comment_author: 'Nur wer den Kommentar geschrieben hat, kann ihn ändern.',
            recap_acknowledgers_not_changeable: 'Der Recap ist noch nicht veröffentlicht.',
            not_acknowledger: 'Du wurdest nicht gebeten, diesen Recap zu bestätigen.',
            not_todo_person: 'Nur die zuständige Person kann das To-do abhaken.',
            already_acknowledged: 'Du hast diesen Recap schon bestätigt.',
        } satisfies Record<ErrorCode, string>,
    },
    validation: {
        required: 'Pflichtfeld',
        routeCondition: 'Füge mindestens eine Bedingung hinzu.',
        routeDestination: 'Wähle einen Kunden oder ein Projekt.',
        sameList: 'Wähle eine andere Liste als die für neue Karten.',
        email: 'Gib eine gültige E-Mail-Adresse ein.',
        slug: 'Verwende nur Kleinbuchstaben, Ziffern und einzelne Bindestriche, höchstens 40 Zeichen.',
        httpsUrl: 'Gib eine Adresse ein, die mit https:// beginnt.',
    },
    // A new workspace's start on the Übersicht (plan task 124).
    start: {
        step: 'Schritt {{step}} von 5',
        steps: {
            forward: 'Weiterleiten',
            clients: 'Kunden',
            invite: 'Einladen',
            publish: 'Veröffentlichen',
            connect: 'Verbinden',
        },
        skip: 'Überspringen',
        hide: 'Start ausblenden',
        forward: {
            title: 'Starte mit ein paar E-Mail-Verläufen',
            lead: 'Leite sie an die Adresse unten weiter. Einer reicht für den Anfang, mit drei oder vier werden die Vorschläge besser. Daraus erkennen wir deine Kunden und Projekte.',
            window: 'Weiterleiten',
            to: 'An',
            subject: 'Betreff',
            subjectExample: 'WG: Lieferzeiten im Checkout',
            waiting: 'Wartet auf die erste E-Mail …',
            sent: 'Ich habe sie weitergeleitet',
            arrivesSoon: 'Kommt meist nach 1–2 Minuten an.',
            soon: 'Die E-Mail sollte gleich da sein.',
            late: 'Hmm, das dauert länger als sonst.',
            openInbox: '{{name}} öffnen',
            noAddress: 'Dieser Workspace hat noch keine Adresse für E-Mails.',
            steps: {
                open: 'Öffne einen E-Mail-Verlauf mit einem Kunden.',
                forward: 'Leite ihn an die Adresse links weiter.',
                more: 'Leite zwei, drei weitere Verläufe weiter, wenn du magst.',
            },
        },
        clients: {
            title: 'Deine ersten E-Mails sind da',
            lead_one:
                'Wir haben einen Kunden darin erkannt und seine Verläufe nach Projekten sortiert. Prüfe kurz, ob Kunden und Projekte stimmen.',
            lead_other:
                'Wir haben {{count}} Kunden darin erkannt und ihre Verläufe nach Projekten sortiert. Prüfe kurz, ob Kunden und Projekte stimmen.',
            leadNone:
                'Darin haben wir noch keinen Kunden erkannt. Ordne die E-Mails selbst zu oder leite weitere weiter.',
            leadSorting:
                'Wir lesen sie gerade und suchen darin deine Kunden, ihre Projekte und wer dazugehört. Erstellt wird nur, was du bestätigst.',
            leadCreated: 'Deine Kunden sind erstellt. Aus deinen E-Mails entstehen gerade die ersten Recaps.',
            reading_one: 'Eine E-Mail wird gelesen …',
            reading_other: '{{count}} E-Mails werden gelesen …',
            sorting: 'Kunden und Projekte werden erkannt …',
            created: 'Erstellt',
            noProjects: 'Ohne Projekte',
            failed: 'Die E-Mails ließen sich nicht sortieren. Ordne sie unten selbst zu.',
            processing_one: 'Eine weitere E-Mail wird noch gelesen. Sie erscheint hier, sobald sie gelesen ist.',
            processing_other:
                '{{count}} weitere E-Mails werden noch gelesen. Sie erscheinen hier, sobald sie gelesen sind.',
            changeLater: 'Namen und Zuordnungen kannst du jederzeit ändern, auch nachdem alles erstellt ist.',
            rename: 'Umbenennen',
            ignore: 'Ignorieren',
            create: 'Erstellen',
            projects: 'Projekte',
            emails: 'E-Mails',
            isNew: 'neu',
            removeProject: 'Nicht erstellen',
            addProject: 'Projekt hinzufügen',
            projectName: 'Name des Projekts',
            clientName: 'Name des Kunden',
            save: 'Übernehmen',
            cancel: 'Abbrechen',
            noProject: 'Ohne Projekt',
            newProject: 'Neues Projekt …',
            toClient: 'Zu „{{client}}“',
            unassigned: 'Ohne Kunde',
            unassignedHint: 'Bei diesen Absendern erkennen wir keine Firma.',
            chooseClient: 'Kunde wählen',
            newClient: 'Neuer Kunde …',
            newClientName: 'Neuer Kunde',
            clientCount_one: '{{count}} Kunde',
            clientCount_other: '{{count}} Kunden',
            projectCount_one: '{{count}} Projekt',
            projectCount_other: '{{count}} Projekte',
            createAll: 'Alle erstellen: {{clients}}, {{projects}}',
            next: 'Weiter',
        },
        invite: {
            title: 'Lade ein, wer mitliest',
            lead: 'Die Adressen kennen wir aus deinen E-Mails. Alle, die du jetzt einlädst, bekommen deinen ersten Recap gleich mit.',
            titleTeam: 'Lade dein Team ein',
            leadTeam:
                'Solange du keinen Kunden erstellt hast, kennen wir dort noch niemanden. Dein Team kannst du schon einladen.',
            team: 'Dein Team',
            teamHint: 'Eingeladene Mitglieder schreiben die Recaps mit dir.',
            teamNone: {
                title: 'In deinen E-Mails haben wir niemanden aus deinem Team gefunden',
                description: 'Füge Mitglieder selbst hinzu oder teile den Einladungslink.',
            },
            firstRecap: 'Eingeladene Personen bekommen zuerst den Recap zu „{{project}}“.',
            clientRecaps: 'Eingeladene Personen bekommen die Recaps von „{{client}}“.',
            invite: 'Einladen',
            edit: 'Bearbeiten',
            remove: 'Nicht einladen',
            addPerson: 'Person hinzufügen',
            name: 'Name',
            email: 'E-Mail-Adresse',
            jobTitle: 'Position',
            cancel: 'Abbrechen',
            apply: 'Übernehmen',
            inviteAll_one: 'Alle einladen: {{count}} Person',
            inviteAll_other: 'Alle einladen: {{count}} Personen',
            next: 'Weiter',
            suggestionsWaiting_one:
                'Ein vorgeschlagener Kunde wartet noch. Sobald du ihn erstellt hast, kannst du auch seine Personen einladen.',
            suggestionsWaiting_other:
                '{{count}} vorgeschlagene Kunden warten noch. Sobald du sie erstellt hast, kannst du auch ihre Personen einladen.',
            showSuggestions: 'Vorschläge ansehen',
        },
        publish: {
            title: 'Veröffentliche deinen ersten Recap',
            lead: 'Sobald deine Einladung angenommen ist, lesen deine Kunden ihn auf Recap und bestätigen ihn mit einem Klick.',
            leadAlone:
                'Noch hast du niemanden eingeladen. Wenn du ihn veröffentlichst, sieht ihn erst einmal nur dein Team.',
            drafting: 'Aus deinen E-Mails entsteht gerade dein erster Recap.',
            none: 'Noch gibt es keinen Entwurf. Er entsteht, sobald du einen Kunden erstellt hast.',
            decisions: 'Entscheidungen',
            todos: 'To-dos',
            others_one: 'Danach wartet noch ein Entwurf',
            others_other: 'Danach warten noch {{count}} Entwürfe',
            publish: 'Veröffentlichen',
            invite: 'Personen einladen',
        },
        connect: {
            title: 'Verbinde deine Tools',
            lead: 'Was in deinen Tools passiert, kommt dann von selbst bei Recap an: Deine Recaps werden vollständiger, und du musst nichts mehr weiterleiten oder hochladen.',
            mailbox: 'Dein Postfach',
            mailboxHint: 'Neue E-Mails kommen von selbst an, ohne dass du sie weiterleitest.',
            otherMailbox: 'Anderes Postfach',
            otherMailboxAction: 'Weiterleitung einrichten',
            assistant: 'Recap in deinem KI-Assistenten',
            assistantHint: 'Er schickt Quellen, liest und erstellt Recaps und hakt To-dos ab.',
            otherAssistant: 'Anderer Assistent',
            otherAssistantAction: 'Über MCP verbinden',
            connectWith: 'Mit {{name}} verbinden',
            more: 'Weitere Tools, aus denen Recap liest',
            allConnections: 'Alle Verbindungen',
            reasons: {
                notion: 'Meeting-Notizen und Seiten werden zu Quellen.',
                trello: 'Karten werden zu Quellen und To-dos zu neuen Karten.',
                hubspot: 'Hält deine Kunden und ihre Ansprechpersonen aktuell.',
            },
            connect: 'Verbinden',
            connected: 'Verbunden',
            dismiss: 'Nicht vorschlagen',
            draftsWaiting_one: 'Ein Entwurf wartet unter „Recaps“, bis du ihn veröffentlichst.',
            draftsWaiting_other: '{{count}} Entwürfe warten unter „Recaps“, bis du sie veröffentlichst.',
            toRecaps: 'Zu den Recaps',
            finish: 'Start abschließen',
        },
    },
    // The start page for whoever isn't signed in (plan task 65).
    landing: {
        signIn: 'Anmelden',
        signUp: 'Zugang anfragen',
        toDark: 'Dunkles Design einschalten',
        toLight: 'Helles Design einschalten',
        hero: {
            tasks: 'Aufgaben in',
            talk: 'Absprachen in',
            files: 'Dateien in',
            question: 'Updates an den Kunden?',
            questionEnd: 'In Handarbeit.',
            otherTools: 'Andere Anwendungen zeigen',
            text: 'Recap macht aus deinen E-Mails, Notizen und Transkripten einen Recap für dich und deinen Kunden: was besprochen und entschieden wurde, wer was zu tun hat und welche Termine anstehen. Du gibst ihn frei, und dein Kunde bestätigt ihn mit einem Klick.',
        },
        samples: {
            label: 'Beispiel',
            from: 'Aus',
            showAll: 'Alles anzeigen',
        },
        how: {
            eyebrow: 'So funktioniert’s',
            replay: 'Noch einmal abspielen',
            title: 'Du arbeitest wie gewohnt, Recap macht den Rest',
            text: 'Bei der Arbeit kommt einiges zusammen: E-Mails, Agenden, Protokolle, Status-Updates und Notizen aus Meetings und Telefonaten. Recap bringt das für deinen Kunden auf den Punkt.',
            incoming: {
                title: 'Was ohnehin anfällt',
                text: 'E-Mails, Notizen und Transkripte kommen aus den Anwendungen, die du verbunden hast. Alles andere kannst du hochladen, auch Fotos und Sprachnotizen.',
            },
            drafted: {
                title: 'Ein Entwurf entsteht',
                text: 'Der Entwurf fasst zusammen, was besprochen wurde, mit den Entscheidungen, To-dos und Terminen. Zu jedem Punkt zeigt er die Fundstelle in den Quellen.',
                meta: 'Aus 3 Quellen, vor 2 Minuten',
            },
            published: {
                title: 'Du prüfst und veröffentlichst',
                text: 'Du änderst, was nicht passt, und wählst, wer bei deinem Kunden bestätigen soll. Erst wenn du veröffentlichst, sieht dein Kunde den Recap.',
                whoAcknowledges: 'Wer soll bestätigen?',
                publish: 'Version 1 veröffentlichen',
            },
            acknowledged: {
                title: 'Dein Kunde bestätigt',
                text: 'Er bekommt eine E-Mail, liest den Recap, kommentiert ihn und bestätigt ihn. Damit ist vereinbart, was darin steht.',
                comments: '2 Kommentare',
            },
            sources: {
                email: 'Re: Relaunch – Fotos',
                emailMeta: 'Tim Müller · E-Mail',
                notes: 'Kick-off Bäckerei Müller',
                notesMeta: 'Notion · Meeting-Notizen',
                transcript: 'Call zu den Tortenbestellungen',
                transcriptMeta: 'Teams · Transkript',
            },
        },
        tools: {
            eyebrow: 'Anwendungen',
            title: 'Integriert sich problemlos in die bereits genutzten Anwendungen',
            text: 'Du verbindest jede Anwendung einmal und wählst, was Recap davon lesen darf. Neues landet danach von selbst beim richtigen Kunden.',
            imap: { name: 'E-Mail-Postfach', text: 'E-Mails mit Anhängen, aus Gmail oder jedem anderen Postfach' },
            microsoft: {
                name: 'Microsoft 365',
                text: 'E-Mails aus Outlook, Transkripte aus Teams und Dateien aus OneDrive',
            },
            google: { name: 'Google Drive', text: 'Dateien, die du auswählst' },
            notion: { name: 'Notion', text: 'Seiten und Meeting-Notizen, sobald niemand mehr daran arbeitet' },
            atlassian: {
                name: 'Jira und Confluence',
                text: 'Arbeitselemente und was mit ihnen passiert ist, dazu die Seiten eines Bereichs',
            },
            trello: { name: 'Trello', text: 'Karten mit ihren Kommentaren und Änderungen' },
            github: { name: 'GitHub', text: 'Issues und Pull Requests der Repositorys, die du freigibst' },
            odoo: { name: 'Odoo', text: 'Kunden, Notizen, E-Mails und Aufgaben mit erfassten Zeiten' },
            hubspot: { name: 'HubSpot', text: 'Deine Kunden und ihre Ansprechpersonen, ohne sie neu anzulegen' },
            webhook: { name: 'Webhooks', text: 'Alles, was Make, n8n oder Zapier schicken' },
            api: { name: 'API und MCP', text: 'Zugriff auf Recaps und Quellen für eigene Skripte und KI-Assistenten' },
            upload: { name: 'Hochladen', text: 'PDFs, Fotos, Office-Dateien und Sprachnotizen' },
        },
        client: {
            eyebrow: 'Für deinen Kunden',
            title: 'Statt „Wie weit seid ihr?“',
            text: 'Nach jedem Termin kommen dieselben Rückfragen: Was hatten wir besprochen, und wer kümmert sich jetzt worum? Mit Recap können du und dein Kunde das jederzeit nachlesen.',
            without: 'Ohne Recap',
            with: 'Mit Recap',
            yours: 'Bei dir',
            acknowledged: '{{name}} hat den Recap bestätigt',
            summary: {
                title: 'Was besprochen wurde',
                text: 'Sobald du einen Recap veröffentlichst, bekommt dein Kunde eine E-Mail und liest mit einem Klick, was besprochen und entschieden wurde.',
            },
            todos: {
                title: 'Was bei ihm liegt',
                text: 'Recap erinnert deinen Kunden automatisch an fällige To-dos und anstehende Termine. Du musst nicht mehr nachhaken.',
            },
            acknowledgement: {
                title: 'Bestätigt mit einem Klick',
                text: 'Recap hält fest, wer welche Version wann bestätigt hat. So ist für alle nachvollziehbar dokumentiert, was vereinbart wurde.',
            },
        },
        try: {
            title: 'Probier Recap mit deinen eigenen E\u2011Mails und Notizen aus',
            text: 'Leite nach der Anmeldung ein paar E\u2011Mails mit deinen Kunden an Recap weiter. Recap erkennt daraus deine Kunden und Projekte und entwirft deinen ersten Recap.',
            beta: 'Kostenlos, solange Recap in der Beta ist.',
            access: 'Neue Workspaces schalten wir nach und nach frei.',
            examples: {
                notes: 'Meeting-Notizen',
                email: 'E-Mail vom Kunden',
                voice: 'Sprachnotiz',
                transcript: 'Gesprächsprotokoll',
                photo: 'Foto vom Whiteboard',
                call: 'Telefonnotiz',
            },
            scraps: {
                notes: '– Startseite neu\n– Filialfinder?\n– Live zum 1.12.!',
                emailSubject: 'Re: Relaunch – Fotos',
                emailText: 'Hallo Lena, anbei die Fotos der Filialen. Am Markt fehlt noch eins …',
                transcript: '14:32 Sabine: Brot auch online? Nein, das wird zu viel. Dann nur Torten.',
                call: 'Sabine, 12 Min.: Preise kommen bis Montag',
            },
        },
        footer: {
            text: 'Recap macht aus dem, was in deinen Anwendungen passiert, Recaps für deine Kunden.',
            product: 'Recap',
            copyright: '© {{year}} Recap',
            language: 'Sprache',
        },
        otherLanguage: 'Read in English',
    },
    legal: {
        title: 'Rechtliches',
        names: {
            privacy: 'Datenschutzerklärung',
            imprint: 'Impressum',
            terms: 'AGB',
            dpa: 'Auftragsverarbeitung',
        },
        date: 'Stand: {{date}}',
        draft: 'Entwurf: Dieser Text wird noch rechtlich geprüft.',
        summary: 'Kurz gesagt',
        summaryNote: 'Die Zusammenfassung erklärt; verbindlich ist der Text darunter.',
        details: 'Im Einzelnen',
        expandAll: 'Alle aufklappen',
        collapseAll: 'Alle zuklappen',
        germanIsBinding: 'Verbindlich ist die deutsche Fassung.',
        acceptTerms: 'Ich akzeptiere die <terms>AGB</terms> und den <dpa>AV-Vertrag</dpa>.',
        settingsHint: 'Was für Recap gilt und wie wir mit deinen Daten umgehen.',
    },
    changelog: {
        title: 'Changelog',
        lead: 'Was sich an Recap geändert hat, das Neueste zuerst.',
        also: 'Außerdem',
    },
    inviteLink: {
        title: 'Einladungslink',
        create: 'Link erstellen',
        renew: 'Neuen Link erstellen',
        turnOff: 'Link deaktivieren',
    },
    members: {
        title: 'Team',
        linkNone: 'Wer den Link hat, kann mit dem eigenen Konto als Mitglied beitreten.',
        linkJoins: 'Wer den Link öffnet und sich anmeldet, tritt {{workspace}} bei.',
        hintOwner:
            'Alle, die Zugriff auf diesen Workspace haben. Als Inhaber:in lädst du sie ein und verwaltest sie. Wer an welchem Kunden und Projekt arbeitet, legst du beim Kunden und beim Projekt im Reiter „Team“ fest.',
        hintMember:
            'Alle, die Zugriff auf diesen Workspace haben. Einladen und verwalten kann sie nur, wem der Workspace gehört. Wer an welchem Kunden und Projekt arbeitet, legst du beim Kunden und beim Projekt im Reiter „Team“ fest.',
        clients: {
            all: 'Alle Kunden',
            none: 'Noch an keinem Kunden',
        },
        you: 'du',
        roles: {
            owner: 'Inhaber:in',
        },
        disabled: 'Deaktiviert',
        disable: {
            action: 'Deaktivieren',
            title: '{{name}} deaktivieren?',
            confirm:
                '{{name}} kann den Workspace dann nicht mehr öffnen und bekommt keine E-Mails mehr daraus. Persönliche Verbindungen werden pausiert. Der Name bleibt überall sichtbar, wo {{name}} etwas getan hat. Du kannst den Zugang jederzeit wieder aktivieren.',
            employeeConfirm:
                '{{name}} kann den Workspace dann nicht mehr öffnen, bekommt keine E-Mails mehr daraus und wird nicht mehr gebeten, Recaps zu bestätigen. Persönliche Verbindungen werden pausiert. Der Name bleibt überall sichtbar, wo {{name}} etwas getan hat. Du kannst den Zugang jederzeit wieder aktivieren.',
            botConfirm:
                'Der Schlüssel seiner Verbindung funktioniert dann nicht mehr, und Recap lehnt jede Anfrage damit ab. Der Name bleibt überall sichtbar, wo {{name}} etwas getan hat. Du kannst den Bot jederzeit wieder aktivieren.',
            submit: 'Deaktivieren',
        },
        enable: {
            action: 'Aktivieren',
        },
        invited: 'Eingeladen',
        invitedOn: 'Eingeladen am {{date}}',
        revokeInvitation: 'Einladung zurückziehen',
        copyInvitationLink: 'Einladungslink kopieren',
        sendInvitation: 'Erneut senden',
        invitationSent: 'Einladung erneut gesendet',
        fields: {
            email: 'E-Mail-Adresse',
        },
        invite: {
            action: 'Einladen',
            title: 'Mitglied einladen',
            hint: 'Wir schicken eine Einladung an diese Adresse. Beitreten kann nur, wer mit dieser Adresse angemeldet ist.',
            submit: 'Einladung senden',
        },
        link: {
            title: 'Einladung gesendet',
            hint: 'Wir haben die Einladung an {{email}} geschickt. Falls sie nicht ankommt, kannst du den Link auch selbst weitergeben – beitreten kann damit trotzdem nur {{email}}.',
            label: 'Einladungslink',
        },
        remove: {
            action: 'Entfernen',
            title: 'Mitglied entfernen?',
            confirm: '{{name}} hat danach keinen Zugriff mehr auf diesen Workspace.',
            submit: 'Entfernen',
        },
        bot: {
            pill: 'Bot',
            withoutConnection: 'Keine Verbindung',
            openConnection: 'Verbindung öffnen',
        },
        deleteBot: {
            action: 'Löschen',
            title: '{{name}} löschen?',
            confirm:
                'Seine Verbindung wird gelöscht, und sein Schlüssel funktioniert nicht mehr. Alles, was {{name}} getan hat, bleibt erhalten und wird als „Gelöschter Bot“ angezeigt.',
            submit: 'Löschen',
        },
        transfer: {
            action: 'Workspace übertragen',
            title: 'Workspace übertragen?',
            confirm:
                'Der Workspace gehört dann {{name}}. Nur noch {{name}} kann Mitglieder einladen und entfernen und den Workspace löschen. Du bleibst Mitglied.',
            submit: 'Übertragen',
        },
        leave: {
            action: 'Workspace verlassen',
            title: 'Workspace verlassen?',
            confirm: 'Du hast danach keinen Zugriff mehr auf {{name}}, bis du wieder eingeladen wirst.',
            submit: 'Verlassen',
        },
    },
    workspaces: {
        accessDisabled: {
            title: 'Zugang deaktiviert',
            description:
                'Dein Zugang zu {{name}} wurde deaktiviert. Wende dich an jemanden dort, um ihn wieder freischalten zu lassen.',
            back: 'Zu deinen Workspaces',
        },
        title: 'Workspace',
        switch: 'Workspace wechseln (aktuell: {{name}})',
        new: 'Neuer Workspace',
        newTitle: 'Neuer Workspace',
        details: 'Angaben',
        shortId: 'ID {{id}}…',
        idHint: 'Für die API: Die ID steht in den Pfaden der Endpunkte und ändert sich nie – anders als das Kürzel.',
        editTitle: 'Workspace bearbeiten',
        newHint:
            'Ein Workspace hat eigene Mitglieder, Verbindungen und Recaps – zum Beispiel für eine Firma oder einen Verein.',
        fields: {
            id: 'Workspace-ID',
            name: 'Name',
            nameHint: 'Zum Beispiel deine Firma, dein Verein oder dein Name',
            slug: 'Kürzel',
            slugHint: 'Erscheint in der Adresse jeder Seite: <path />',
            logo: 'Logo',
        },
        logo: {
            choose: 'Logo auswählen',
            hint: 'Wird statt der Farbe angezeigt. PNG, JPEG oder WebP, bis 2 MB.',
        },
        delete: {
            action: 'Workspace löschen',
            title: 'Workspace löschen?',
            confirm:
                '{{name}} wird mit allen Recaps, Dateien und Verbindungen gelöscht. Das lässt sich nicht rückgängig machen.',
            typeSlug: 'Gib zur Bestätigung „{{slug}}“ ein',
            submit: 'Endgültig löschen',
            blockedTitle: 'Workspace kann nicht gelöscht werden',
            blocked: '{{name}} kann nur löschen, wem der Workspace gehört.',
        },
    },
    settings: {
        tabs: {
            label: 'Bereiche',
            general: 'Allgemein',
            connectionFailed: 'Bei einer Verbindung gibt es einen Fehler',
        },
        dangerZone: {
            deleteHint: 'Löscht alle Recaps, Dateien und Verbindungen. Das kann nur, wem der Workspace gehört.',
            leaveHint: 'Du hast danach keinen Zugriff mehr auf den Workspace. Löschen kann ihn nur, wem er gehört.',
        },
    },
    connections: {
        title: 'Verbindungen',
        hint: 'Verbinde die Tools, mit denen dein Team arbeitet, zum Beispiel ein Postfach (IMAP). Was dort ankommt, wird zu Quellen.',
        empty: 'Noch keine Verbindungen',
        ownTitle: 'Meine Verbindungen',
        membersConnections: 'Verbindungen von Mitgliedern',
        ownEmpty: 'Noch keine eigenen Verbindungen',
        ownHint:
            'Verbinde deine eigenen Tools, zum Beispiel deinen Kalender, um dort zu sehen, was dich in diesem Workspace betrifft.',
        add: {
            button: 'Verbindung hinzufügen',
            title: 'Verbindung hinzufügen',
            notSetUp: 'Auf diesem Server nicht eingerichtet',
            search: 'Suchen',
            noResults: 'Nichts gefunden',
            ownTools: 'Für eigene Tools',
            connected: 'Verbunden',
            all: 'Alle',
            pickAccount: {
                title: 'Für welches Konto?',
                description: '{{entry}} kommt zu einem dieser Konten hinzu.',
                other: 'Anderes Konto verbinden',
            },
        },
        connection: {
            newTitle: '{{provider}} verbinden',
            editTitle: '{{provider}} bearbeiten',
            edit: 'Verbindung bearbeiten',
            connect: 'Verbinden',
            connecting: 'Wird verbunden …',
            test: 'Verbindung testen',
            testing: 'Wird getestet …',
            testSucceeded: 'Die Verbindung funktioniert. Du kannst sie jetzt speichern.',
            testRequired: 'Teste die Verbindung, bevor du sie speicherst.',
            noFeeds: {
                description: 'Lege fest, was über diese Verbindung hereinkommt oder hinausgeht.',
                readOnly: 'Was über diese Verbindung hereinkommt oder hinausgeht, legt fest, wer sie bearbeiten darf.',
            },
            toUserSettings: 'Zu den Kontoeinstellungen',
            picksFiles:
                'Über diese Verbindung kannst du auf der Seite „Quellen“ unter „Hinzufügen“ Dateien aus {{provider}} auswählen.',
            signIn: 'Bei {{provider}} anmelden',
            signingIn: 'Wird weitergeleitet …',
            signedOut:
                'Die Verbindung zu {{provider}} funktioniert nicht mehr, zum Beispiel weil der Zugriff entzogen wurde. Verbinde sie neu. Bis dahin wird nichts abgerufen oder gesendet.',
            signInAgain: 'Neu verbinden',
            signedOutState: 'Nicht mehr verbunden',
            switchAccount: {
                button: 'Anderes Konto verbinden',
                title: 'Anderes Konto verbinden?',
                confirm:
                    'Die Verbindung nutzt dann das Konto, mit dem du dich bei {{provider}} anmeldest. Ihre Feeds bleiben erhalten. Was nur das bisherige Konto erreicht, schlägt beim nächsten Abruf fehl.',
                submit: 'Bei {{provider}} anmelden',
            },
            fields: {
                name: 'Name',
            },
            nameHint: 'Wie soll die Verbindung heißen? Zum Beispiel „Buchhaltung“.',
            newNameHint:
                'Wie soll die Verbindung heißen? Zum Beispiel „Buchhaltung“. Wenn du nichts eingibst, heißt sie wie das Konto.',
            feedCount_one: '{{count}} Feed',
            feedCount_other: '{{count}} Feeds',
            clientCount_one: 'für einen Kunden',
            clientCount_other: 'für {{count}} Kunden',
            errorCount_one: 'Fehler',
            errorCount_other: '{{count}} Fehler',
            directions: {
                input: 'Eingehend',
                output: 'Ausgehend',
            },
            delete: {
                button: 'Verbindung löschen',
                title: 'Verbindung löschen?',
                confirm:
                    'Danach wird nichts mehr darüber abgerufen. Bereits abgerufene Quellen und die Recaps daraus bleiben erhalten.',
                confirmMember:
                    'Die Verbindung gehört {{name}}. Danach ruft Recap darüber nichts mehr ab und schickt nichts mehr dorthin, und {{name}} bekommt eine E-Mail darüber. Bereits abgerufene Quellen und die Recaps daraus bleiben erhalten.',
                submit: 'Löschen',
            },
        },
        feed: {
            pickTitle: 'Feed zu {{name}} hinzufügen',
            access: {
                hint: 'Dafür braucht Recap deine Erlaubnis bei {{provider}}. Du meldest dich dort erneut an und kommst dann hierher zurück.',
                allow: 'Zugriff erlauben',
            },
            flows: {
                input: '{{name}} → Recap',
                output: 'Recap → {{name}}',
            },
            add: 'Hinzufügen',
            addMore: 'Feed hinzufügen',
            fetchesFrom: 'Abruf ab {{date}}',
            syncedAt: 'Zuletzt abgerufen {{date}}',
            linkedClients: 'Zu den verknüpften Kunden',
            onPublish: 'Beim Veröffentlichen',
            ownRecaps: 'Deine Recaps',
            allRecaps: 'Alle Recaps',
            everythingElse: 'Alles andere',
            syncedUntilHint: 'Wähle ein früheres Datum, um auch ältere Einträge abzurufen.',
            defaultClient: 'Standardkunde',
            defaultProject: 'Standardprojekt',
            drafting: {
                label: 'Recap entwerfen',
                hint: 'Sobald die Quelle gelesen ist.',
                none: 'Nein',
                alone: 'Nur aus dieser Quelle',
                withWaitingInProject: 'Zusammen mit allen wartenden Quellen in {{project}}',
                withWaitingOfClient:
                    'Zusammen mit allen wartenden Quellen von {{client}}, die keinem Projekt zugeordnet sind',
                withWaitingWithoutClient: 'Zusammen mit allen wartenden Quellen ohne Kunde',
                shownAlone: 'entwirft einen Recap',
                shownWithWaiting: 'entwirft einen Recap mit den wartenden Quellen',
            },
            skipsHint: 'Sonst wird nur importiert, worauf eine Regel zutrifft. Alles andere wird nicht gespeichert.',
            skipped: 'Wird übersprungen',
            skipsEverything: 'Alles wird übersprungen',
            byWebhook: 'Kommt per Webhook',
            byForwarding: 'Kommt per Weiterleitung',
            waitsForReconnect: 'Wartet, bis die Verbindung neu verbunden ist',
            failed: 'Fehler',
            fields: {
                synced_until: 'Abrufen ab',
            },
            routes: {
                add: 'Regel hinzufügen',
                newTitle: 'Regel hinzufügen',
                editTitle: 'Regel bearbeiten',
                noClient: 'Kein Kunde',
                quoted: '„{{keyword}}“',
                senders: 'Von',
                organizer: 'Organisiert von',
                attendees: 'Teilnehmende',
                recipients: 'An',
                subjectKeywords: 'Betreff',
                bodyKeywords: 'Text',
                fileName: 'Dateiname',
                titleKeywords: 'Titel',
                lists: 'Liste',
                hint: 'Eine Quelle gehört zu dieser Regel, wenn in einer ihrer Gruppen jede Bedingung zutrifft; in einer Bedingung reicht einer der Einträge.',
                hintEmail:
                    'Eine E-Mail gehört zu dieser Regel, wenn in einer ihrer Gruppen jede Bedingung zutrifft; in einer Bedingung reicht einer der Einträge.',
                words: 'Enthält die Wörter',
                notWords: 'Enthält nicht',
                query: 'Suchanfrage',
                addCondition: 'Hinzufügen',
                conditionSection: 'Bedingung',
                groupSection: 'Gruppe',
                orGroup: 'Oder-Gruppe',
                or: 'oder',
                removeCondition: '{{field}} entfernen',
                convert: 'In Suchanfrage umwandeln',
                convertGroup: 'Gruppe in Suchanfrage umwandeln',
                convertTitle: 'In eine Suchanfrage umwandeln?',
                convertConfirm:
                    'Die Bedingungen werden zu einer Suchanfrage, die dieselben E-Mails trifft, und ihre Felder verschwinden. Das lässt sich nicht rückgängig machen.',
                convertGroupConfirm:
                    'Die Bedingungen dieser Gruppe werden zu einer Suchanfrage, die dieselben E-Mails trifft, und ihre Felder verschwinden. Die anderen Gruppen bleiben, wie sie sind. Das lässt sich nicht rückgängig machen.',
                convertSubmit: 'Umwandeln',
                querySyntax: {
                    title: 'So schreibst du eine Suchanfrage',
                    from: 'Von dieser Domain, Adresse oder diesem Namen',
                    to: 'An diese Domain, Adresse oder diesen Namen, auch in Cc oder als Bcc',
                    subject: 'Dieses Wort im Betreff',
                    filename: 'Dieses Wort im Namen eines Anhangs',
                    anywhere: 'Dieses Wort irgendwo in der E-Mail',
                    phrase: 'Genau diese Wörter',
                    or: 'Eins von beiden',
                    and: 'Beides (auch mit AND)',
                    not: 'Ohne dieses Wort (auch mit NOT)',
                    field: 'Ein Feld für mehrere Wörter',
                },
                queryErrors: {
                    unclosed_bracket: 'Diese Klammer wird nicht geschlossen. Setze ein „)“ an ihr Ende.',
                    unexpected_bracket: 'Zu dieser Klammer fehlt das „(“. Entferne sie oder setze ein „(“ davor.',
                    unclosed_quote:
                        'Das Anführungszeichen wird nicht geschlossen. Setze ein zweites hinter die Wörter.',
                    unknown_field: '„{{name}}:“ gibt es nicht. Verwende from:, to:, subject: oder filename:.',
                    field_in_field:
                        'In den Klammern eines Felds kann kein weiteres Feld stehen. Schreib die Felder nebeneinander.',
                    missing_value: 'Nach „{{text}}“ fehlt, wonach gesucht wird, zum Beispiel from:acme.de.',
                    dangling_operator:
                        '„{{text}}“ braucht einen Begriff, auf den es sich bezieht. Ergänze ihn oder entferne „{{text}}“.',
                    too_long: 'Eine Suchanfrage hat höchstens 2000 Zeichen. Kürze sie oder verteile sie auf Gruppen.',
                },
                preview: {
                    emails: 'Trifft {{count}} der letzten {{total}} E-Mails',
                    emailsNone: 'Noch keine E-Mails, an denen sich die Regel testen lässt.',
                    sources: 'Trifft {{count}} der letzten {{total}} Quellen',
                    sourcesNone: 'Noch keine Quellen, an denen sich die Regel testen lässt.',
                    show: 'Testen',
                    hide: 'Ausblenden',
                },
                delete: {
                    button: 'Löschen',
                    title: 'Regel löschen?',
                    confirm: 'Bereits zugeordnete Einträge behalten ihren Kunden.',
                    submit: 'Löschen',
                },
            },
            delete: {
                button: 'Löschen',
                title: 'Feed löschen?',
                confirm: 'Danach wird nichts mehr darüber abgerufen. Bereits abgerufene Quellen bleiben erhalten.',
                submit: 'Löschen',
            },
        },
        facets: {
            email: {
                label: 'E-Mails',
                keepsEverything: 'Alle eingehenden E-Mails importieren',
            },
            meeting: {
                label: 'Meetings',
                keepsEverything: 'Alle aufgezeichneten Meetings importieren',
            },
            document: {
                label: 'Dokumente',
                keepsEverything: 'Alle neuen Dokumente importieren',
            },
            task: {
                label: 'Tickets',
                keepsEverything: 'Alle Tickets importieren',
            },
            conversation: {
                label: 'Unterhaltungen',
                keepsEverything: 'Alle Unterhaltungen importieren',
            },
            calendar: { label: 'Kalender', keepsEverything: 'Alle Besprechungen importieren' },
            events: { label: 'Termine' },
            todos: { label: 'To-dos' },
            recaps: { label: 'Recaps' },
            clients: { label: 'Kunden' },
        },
        errors: {
            login_failed:
                'Der letzte Abruf ist fehlgeschlagen: Die Anmeldung wurde abgelehnt. Prüfe Benutzername und Passwort.',
            unreachable: 'Der letzte Abruf ist fehlgeschlagen: Der Server war nicht erreichbar.',
            not_found: 'Der letzte Abruf ist fehlgeschlagen: Was dieser Feed liest, gibt es nicht mehr.',
            host_not_allowed:
                'Der letzte Abruf ist fehlgeschlagen: Der Server ist aus dem Internet nicht mehr erreichbar.',
            sync_failed: 'Der letzte Abruf ist wegen eines Fehlers bei uns fehlgeschlagen. Versuche es noch einmal.',
            unsupported_version: 'Der letzte Abruf ist fehlgeschlagen: Diese Version des Servers liest Recap nicht.',
            api_not_included: 'Der letzte Abruf ist fehlgeschlagen: Der Tarif hat keine externe API mehr.',
        },
        atlassian: {
            summary: 'Jira und Confluence, mit deiner Atlassian-Anmeldung',
            description:
                'Ein Atlassian-Konto. Du meldest dich bei Atlassian an und wählst dort die Site, die Recap lesen darf. Eine weitere Site fügst du hinzu, indem du dich erneut anmeldest und sie auswählst. Was Recap lesen darf, fragt es erst an, wenn du etwas hinzufügst. Recap liest nur und ändert nichts. Löschst du die Verbindung, entferne Recap auch in deinem Atlassian-Konto unter „Apps“, denn Atlassian behält den Zugriff bis dahin.',
            workspaceNote:
                'Eine Atlassian-Verbindung nutzt ein einzelnes Atlassian-Konto und erreicht nur, worauf dieses Konto Zugriff hat. Verbinde für den Workspace deshalb ein gemeinsames Konto (zum Beispiel projekte@agentur.de), nicht dein eigenes.',
            entries: {
                task: { name: 'Jira', summary: 'Arbeitselemente eines Projekts und was mit ihnen passiert ist' },
                document: { name: 'Confluence', summary: 'Seiten eines Bereichs, sobald niemand mehr daran arbeitet' },
            },
            fields: { jiraProject: 'Jira-Projekt', spaceOrPage: 'Bereich oder Seite' },
            pickProject: 'Jira-Projekt auswählen',
            projectsLoading: 'Jira-Projekte werden geladen …',
            projectHint: 'Fehlt ein Projekt? Melde dich erneut bei Atlassian an und wähle dort seine Site aus.',
            spaceOrPageHint:
                'Der Link eines Bereichs oder einer Seite aus der Adresszeile deines Browsers. Bei einer Seite werden alle Seiten darunter mitgelesen.',
            settleHint: 'Eine Seite wird erst importiert, wenn so lange niemand mehr daran gearbeitet hat.',
            changeTrackingHint:
                'Ändert sich eine Seite später, wird ihre Quelle markiert, und du kannst die Änderung auf der Quelle übernehmen.',
            feeds: {
                task: {
                    label: 'Arbeitselemente lesen',
                    description:
                        'Jede Änderung an einem Arbeitselement eines Jira-Projekts wird zu einer Quelle. Was nur ein Teil des Teams sehen darf, bleibt draußen.',
                },
                document: {
                    label: 'Seiten lesen',
                    description: 'Jede Seite eines Bereichs wird zur Quelle, sobald niemand mehr daran arbeitet.',
                },
            },
        },
        github: {
            summary: 'Issues und Pull Requests der Repositorys, die du freigibst',
            description:
                'Recap wird als GitHub App in deiner Organisation oder deinem Konto installiert, und du wählst bei GitHub die Repositorys aus, die Recap lesen darf. Recap liest ihre Issues und Pull Requests, aber keinen Code. GitHub zeigt dabei auch Schreibrechte für Issues an: Die braucht Recap erst, wenn es To-dos als Issues anlegt.',
            repositoryHint: 'Fehlt ein Repository? <page>Gib es bei GitHub frei</page>.',
            pickRepository: 'Repository auswählen',
            repositoriesLoading: 'Repositorys werden geladen …',
            fields: {
                repository: 'Repository',
            },
            feeds: {
                task: {
                    label: 'Issues und Pull Requests lesen',
                    description:
                        'Jede Änderung an einem Issue oder Pull Request eines Repositorys wird zu einer Quelle.',
                },
            },
        },
        google: {
            summary: 'Dateien aus Google Drive, die du auswählst',
            workspaceNote:
                'Eine Google-Verbindung nutzt ein einzelnes Google-Konto und erreicht nur, worauf dieses Konto Zugriff hat. Verbinde für den Workspace deshalb ein gemeinsames Konto (zum Beispiel team@agentur.de), nicht dein eigenes. Ihr eigenes Google-Konto verbindet jede Person in ihren Kontoeinstellungen.',
            description:
                'Ein Google-Konto. Du meldest dich bei Google an, und Recap erfährt dabei nur deine E-Mail-Adresse. Weitere Rechte fragt Recap erst an, wenn du etwas einrichtest, das sie braucht.',
        },
        microsoft: {
            summary: 'Outlook.com und Microsoft 365, mit deiner Microsoft-Anmeldung',
            description:
                'Ein Microsoft-Konto, Outlook.com oder Microsoft 365. Du meldest dich bei Microsoft an, und Recap erfährt dabei nur, wer du bist. Was es lesen darf, fragt Recap erst an, wenn du etwas hinzufügst, etwa einen Ordner deines Postfachs. In deinem Postfach markiert Recap nichts als gelesen und verschiebt nichts.',
            workspaceNote:
                'Eine Verbindung zu Microsoft 365 nutzt ein einzelnes Microsoft-Konto und erreicht nur, worauf dieses Konto Zugriff hat. Verbinde für den Workspace deshalb ein gemeinsames Konto (zum Beispiel projekte@agentur.de), nicht dein eigenes. Ihr eigenes Konto verbindet jede Person in ihren Kontoeinstellungen.',
            entries: {
                email: { name: 'Outlook', summary: 'E-Mails aus einem Ordner deines Postfachs' },
                calendar: { name: 'Outlook-Kalender', summary: 'Besprechungen mit Agenda, sobald sie vorbei sind' },
                meeting: {
                    name: 'Teams-Besprechungen',
                    summary: 'Transkripte und Agenda deiner Teams-Besprechungen, sobald sie vorbei sind',
                },
                document: {
                    name: 'OneDrive & SharePoint',
                    summary: 'Dateien aus einem Ordner, sobald niemand mehr daran arbeitet',
                },
                todos: { name: 'Microsoft To Do', summary: 'Deine To-dos als Aufgaben in einer Liste, zum Abhaken' },
            },
            folderLinkHint:
                'Der Link des Ordners aus der Adresszeile deines Browsers oder über „Link kopieren“ in OneDrive oder SharePoint. Unterordner werden mitgelesen.',
            settleHint: 'Eine Datei wird erst importiert, wenn so lange niemand mehr daran gearbeitet hat.',
            changeTrackingHint:
                'Ändert sich eine Datei später, wird ihre Quelle markiert, und du kannst die Änderung auf der Quelle übernehmen.',
            meetingHint:
                'Teams schreibt ein Transkript nur, wenn jemand in der Besprechung die Transkription startet. Ohne Transkript kommt eine Besprechung mit ihrer Agenda, sobald Teams zwei Stunden lang keins geschrieben hat.',
            transcriptSteps: {
                title: 'Ohne Verbindung: Transkript selbst hochladen',
                open: 'Öffne in Teams den Chat oder den Kalendereintrag der Besprechung und dort „Recap“ › „Transkript“.',
                download: 'Wähle „Herunterladen“, als .docx oder .vtt.',
                upload: 'Lade die Datei in Recap unter „Quellen“ › „Hinzufügen“ hoch und wähle Kunde und Projekt der Besprechung.',
            },
            fields: { calendar: 'Kalender', folderLink: 'Ordner', todoList: 'Liste' },
            pickCalendar: 'Kalender auswählen',
            calendarsLoading: 'Kalender werden geladen …',
            calendarHint: 'Besprechungen ohne Agenda, abgesagte und abgelehnte bleiben draußen.',
            pickTodoList: 'Liste auswählen',
            todoListsLoading: 'Listen werden geladen …',
            todoListHint:
                'Deine offenen To-dos kommen als Aufgaben in diese Liste. Hakst du eine Aufgabe dort ab, ist das To-do auch in Recap erledigt.',
            folderHint: 'Am besten ein eigener Ordner, in den eine Regel in Outlook die passenden E-Mails verschiebt.',
            ownNote:
                'Was Recap aus deinem Postfach liest, gehört dem Workspace: Alle, die den Kunden sehen, sehen auch diese E-Mails.',
            signInInstead: 'Outlook verbindest du über deine Anmeldung bei Microsoft, ohne Passwort.',
            signIn: 'Bei Microsoft anmelden',
            adminConsentLink: 'Link zur Freigabe',
            feeds: {
                email: {
                    description: 'Jede E-Mail in einem Ordner des Postfachs wird zu einer Quelle.',
                },
                calendar: {
                    label: 'Besprechungen lesen',
                    description: 'Jede Besprechung mit Agenda wird zur Quelle, sobald sie vorbei ist.',
                },
                meeting: {
                    label: 'Teams-Besprechungen lesen',
                    description:
                        'Jede Teams-Besprechung wird zur Quelle, mit ihrem Transkript, sobald Teams es geschrieben hat. Das Lesen der Transkripte muss meist der Admin deiner Organisation erlauben.',
                    describe: 'Deine Teams-Besprechungen',
                },
                todos: {
                    label: 'To-dos senden',
                    description:
                        'Deine To-dos aus veröffentlichten Recaps werden zu Aufgaben in einer Liste in Microsoft To Do.',
                },
                document: {
                    label: 'Dateien lesen',
                    description:
                        'Jede Datei in einem Ordner wird zur Quelle, sobald niemand mehr daran arbeitet. Word, Excel, PowerPoint, PDFs und Bilder.',
                },
            },
        },
        trello: {
            description:
                'Ein Trello-Konto. Recap liest seine Boards und kann To-dos aus veröffentlichten Recaps als Karten anlegen.',
            signIn: 'Mit Trello verbinden',
            signedIn: 'Bei Trello angemeldet',
            signInHint: 'Melde dich im neuen Fenster bei Trello an und erlaube Recap den Zugriff. Speichere danach.',
            signInAgainHint:
                'Melde dich nur erneut an, wenn der Zugriff entzogen wurde oder du ein anderes Konto verbinden möchtest.',
            notSignedIn: 'Melde dich zuerst bei Trello an.',
            boardHint: 'Der Link des Boards aus der Adresszeile deines Browsers.',
            pickList: 'Liste auswählen',
            doneListHint: 'Karten in dieser Liste gelten als erledigt, und abgehakte To-dos werden hierher verschoben.',
            listsHint:
                'Wenn du keine auswählst, werden alle Listen gelesen. Eine Karte, die eine ausgewählte Liste verlässt, wird trotzdem importiert.',
            listsLoading: 'Listen werden geladen …',
            attachmentsHint:
                'Dateien, die seit dem letzten Abruf zu einer Karte hochgeladen wurden, werden mit importiert: PDFs, Bilder, Office-Dokumente und Textdateien bis 20 MB.',
            listsNeedBoard: 'Gib zuerst den Link des Boards ein.',
            fields: {
                board: 'Board',
                lists: 'Listen',
                attachments: 'Anhänge importieren',
                list: 'Neue Karten in',
                doneList: 'Liste für Erledigtes',
            },
            feeds: {
                task: {
                    label: 'Karten lesen',
                    description: 'Jede Änderung an einer Karte eines Boards wird zu einer Quelle.',
                },
                todos: {
                    label: 'To-dos als Karten anlegen',
                    description:
                        'Die To-dos aus den veröffentlichten Recaps eines Projekts werden zu Karten auf einem Board.',
                },
            },
        },
        notion: {
            description:
                'Ein Notion-Workspace, über eine interne Integration. Recap liest Seiten nur und ändert sie nie.',
            tokenHint: 'Leer lassen, um den gespeicherten Token zu behalten.',
            newTokenHint:
                'Lege unter notion.so/profile/integrations eine interne Integration mit „Inhalte lesen“ an und kopiere ihren Token. Teile dann jede Seite, die Recap lesen soll, mit ihr („•••“ › Verbindungen).',
            pageHint:
                'Der Link der Seite, über „Link kopieren“ in Notion. Die Seite muss mit der Integration geteilt sein.',
            depthHint: 'Mit einer Ebene funktioniert die Seite wie ein Ordner: Jede Unterseite wird zu einer Quelle.',
            settleHint: 'Eine Seite wird erst importiert, wenn so lange niemand mehr daran gearbeitet hat.',
            changeTrackingHint:
                'Ändert sich eine Seite später, wird ihre Quelle markiert, und du kannst die Änderung auf der Quelle übernehmen.',
            fields: {
                token: 'Token',
                page: 'Seite',
                depth: 'Unterseiten',
                settle: 'Importieren, wenn unverändert seit',
                changeTracking: 'Änderungen erkennen',
            },
            depths: {
                only: 'Nur diese Seite',
                one: 'Eine Ebene',
                two: 'Zwei Ebenen',
                three: 'Drei Ebenen',
                all: 'Alle Ebenen',
            },
            settle: {
                now: 'Sofort',
                quarterHour: '15 Minuten',
                hour: '1 Stunde',
                threeHours: '3 Stunden',
                day: '1 Tag',
            },
            feeds: {
                document: {
                    label: 'Seiten lesen',
                    description: 'Eine Seite oder jede ihrer Unterseiten wird zu einer Quelle.',
                },
            },
        },
        hubspot: {
            description:
                'Recap wird als App in euren HubSpot-Account installiert. Was es lesen darf, fragt Recap erst an, wenn du etwas hinzufügst, etwa eure Kunden. Recap liest nur und ändert in HubSpot nichts.',
            entries: {
                clients: { name: 'HubSpot Kunden', summary: 'Deine Kunden und ihre Ansprechpersonen aus HubSpot' },
            },
            fields: {
                lifecycleStages: 'Lebenszyklusphasen',
            },
            lifecycleStagesLoading: 'Lebenszyklusphasen werden geladen …',
            lifecycleStagesHint:
                'Firmen in diesen Phasen kommen als Kunden. Nimm die Phasen davor dazu, wenn ihr schon vor dem Auftrag an einem Projekt arbeitet.',
            feeds: {
                clients: {
                    label: 'Kunden lesen',
                    description:
                        'Deine Kunden aus HubSpot mit ihren Ansprechpersonen. Passt eine Firma eindeutig zu einem Kunden in Recap, wird sie mit ihm verknüpft; die anderen kannst du unter „Kunden“ hinzufügen oder verknüpfen.',
                },
            },
        },
        odoo: {
            description:
                'Euer Odoo auf Odoo.com, Odoo.sh oder einem eigenen Server, über den API-Schlüssel eines Benutzers. Recap liest nur und ändert in Odoo nichts.',
            entries: {
                clients: { name: 'Odoo Kunden', summary: 'Deine Kunden und ihre Ansprechpersonen aus Odoo' },
                conversation: {
                    name: 'Odoo Nachrichten',
                    summary: 'Notizen, Nachrichten und E-Mails zu deinen Kunden',
                },
                task: { name: 'Odoo Projekte', summary: 'Aufgaben und erfasste Zeiten eines Projekts' },
                document: {
                    name: 'Odoo Dokumente',
                    summary: 'Dateien aus einem Ordner in Odoo Dokumente, nur in Odoo Enterprise',
                },
            },
            steps: {
                user: 'Lege in Odoo unter „Einstellungen“ › „Benutzer“ einen eigenen Benutzer für Recap an. Recap liest alles, was dieser Benutzer sehen darf – gib ihm also nur die Rechte für das, was Recap lesen soll.',
                key: 'Melde dich als dieser Benutzer an und wähle oben rechts „Mein Profil“ › „Konto-Sicherheit“ › „Neuer API-Schlüssel“. Wähle die längste Gültigkeit, die Odoo anbietet: Läuft der Schlüssel ab, ruft Recap nichts mehr ab.',
                enter: 'Trage hier die Adresse von Odoo, den Login des Benutzers und den Schlüssel ein.',
            },
            planNote:
                'Auf Odoo.com hat nur der Tarif „Custom“ eine externe API. Mit „Standard“ oder „One App Free“ kann sich Recap nicht verbinden; Odoo.sh und eigene Server gehen immer.',
            serverHint: 'Die Adresse, unter der du Odoo im Browser öffnest, zum Beispiel https://agentur.odoo.com.',
            databaseHint:
                'Bei Odoo.com und bei einem Server mit nur einer Datenbank kannst du das Feld leer lassen. Bei Odoo.sh steht der Name in der Übersicht des Projekts.',
            loginHint: 'Der Login des Benutzers für Recap, meist seine E-Mail-Adresse.',
            apiKeyHint: 'Leer lassen, um den gespeicherten Schlüssel zu behalten.',
            fields: {
                server: 'Server',
                database: 'Datenbank',
                login: 'Benutzer',
                apiKey: 'API-Schlüssel',
                tags: 'Schlagwörter',
                models: 'Lesen von',
                project: 'Projekt',
                folder: 'Ordner',
            },
            models: {
                partner: 'Kontakte',
                lead: 'Leads und Verkaufschancen',
                order: 'Angebote und Aufträge',
                project: 'Projekte',
            },
            pickProject: 'Projekt auswählen',
            pickFolder: 'Ordner auswählen',
            foldersLoading: 'Ordner werden geladen …',
            folderHint: 'Unterordner werden mitgelesen.',
            documentsMissing:
                'Odoo Dokumente gibt es nur in Odoo Enterprise. Auf diesem Server ist die App nicht installiert.',
            settleHint: 'Eine Datei wird erst importiert, wenn so lange niemand mehr daran gearbeitet hat.',
            changeTrackingHint:
                'Ändert sich eine Datei später, wird ihre Quelle markiert, und du kannst die Änderung auf der Quelle übernehmen.',
            projectsLoading: 'Projekte werden geladen …',
            modelsHint: 'Nur, was in Odoo installiert ist. Interne Notizen kommen mit.',
            tagsLoading: 'Schlagwörter werden geladen …',
            tagsHint:
                'Nur Kunden mit einem dieser Schlagwörter. Ohne Auswahl kommen alle Firmen, die bei euch gekauft haben.',
            feeds: {
                clients: {
                    label: 'Kunden lesen',
                    description:
                        'Deine Kunden aus Odoo mit ihren Ansprechpersonen. Passt eine Firma eindeutig zu einem Kunden in Recap, wird sie mit ihm verknüpft; die anderen kannst du unter „Kunden“ hinzufügen oder verknüpfen.',
                    everyCustomer: 'Alle Kunden',
                },
                document: {
                    label: 'Dateien lesen',
                    description:
                        'Jede Datei in einem Ordner von Odoo Dokumente wird zur Quelle, sobald niemand mehr daran arbeitet. Ist ihr Kontakt mit einem Kunden verknüpft, geht sie zu diesem Kunden.',
                },
                task: {
                    label: 'Aufgaben lesen',
                    description:
                        'Jede Aufgabe eines Projekts, an der sich etwas getan hat, wird zu einer Quelle: mit Kommentaren, Änderungen und den erfassten Zeiten.',
                },
                conversation: {
                    label: 'Nachrichten lesen',
                    description:
                        'Was zu einem Datensatz geschrieben wurde (Notizen, Nachrichten, E-Mails, protokollierte Anrufe), wird je Abruf zu einer Quelle. Ist seine Firma mit einem Kunden verknüpft, geht sie zu diesem Kunden.',
                },
            },
        },
        api: {
            description:
                'Für eigene Skripte, n8n und alles andere, was eine API aufrufen kann: Ein Schlüssel gibt Zugriff auf die API von Recap. Die Verbindung des Workspaces handelt als Bot, der unter den Mitgliedern erscheint; deine persönliche handelt in deinem Namen und mit deinen Rechten.',
            name: 'API (Entwickler)',
            summary: 'Ein Schlüssel für Skripte, n8n und Claude Code',
            fields: {
                readOnly: 'Nur lesen: Mit dem Schlüssel lässt sich nichts ändern',
                bot: 'Bot',
            },
            botHint: 'Ein bestehender Bot behält seinen Namen und seinen Verlauf.',
            newBot: 'Neuer Bot',
            token: {
                title: 'Dein API-Schlüssel',
                warning:
                    'Bewahre ihn sicher auf, zum Beispiel in deinem Passwortmanager. Wer ihn hat, kann alles tun, was die Verbindung darf. Wenn du ihn verlierst, erstelle einen neuen.',
                copy: 'Schlüssel kopieren',
                saved: 'Ich habe den Schlüssel gespeichert',
                usage: 'So rufst du die API auf',
                docsLead: 'Alle Endpunkte:',
                docs: 'API-Dokumentation',
            },
            details: {
                token: 'Schlüssel',
                actsAs: 'Handelt als',
                actsAsYou: 'Du, mit deinen Rechten',
                access: 'Rechte',
                readOnly: 'Nur lesen',
                readWrite: 'Lesen und schreiben',
                usedAt: 'Zuletzt verwendet',
                neverUsed: 'Noch nie',
            },
            bot: {
                disabledTitle: 'Der Bot „{{name}}“ ist deaktiviert',
                disabled:
                    'Der Schlüssel funktioniert erst wieder, wenn du den Bot aktivierst. Bis dahin lehnt Recap jede Anfrage damit ab.',
            },
            usage: {
                renew: 'Neuen Schlüssel erstellen',
                apiTitle: 'Über die API',
                api: 'Für eigene Skripte, n8n, Make und alles andere, was eine API aufruft: Schicke Anfragen an {{address}}, mit dem Schlüssel im Header „Authorization: Bearer …“.',
                keyNote:
                    'Verwende den vollständigen Schlüssel statt „{{prefix}}…“. Recap zeigt ihn nur einmal an, beim Erstellen. Nicht mehr da? Erstelle einen neuen.',
                docs: 'API-Dokumentation öffnen',
                mcpTitle: 'Über MCP',
                mcp: 'Für KI-Assistenten: Der MCP-Server von Recap unter {{address}} stellt ihnen Werkzeuge wie „Recaps suchen“ und „To-do abhaken“ zur Verfügung, mit den Rechten dieser Verbindung.',
                madeBy: '{{client}} hat diese Verbindung bei der Anmeldung über MCP erstellt und nutzt sie selbst. Wenn du sie löschst, wird {{client}} getrennt.',
                claudeCode: {
                    name: 'Claude Code',
                    hint: 'im Terminal',
                    command: 'Im Terminal:',
                    check: 'Prüfe in Claude Code mit „/mcp“, ob „recap“ verbunden ist.',
                },
                other: {
                    name: 'Cursor und andere',
                    hint: 'mit dem Schlüssel im Header',
                    config: 'Trage ihn als MCP-Server über HTTP ein, zum Beispiel in Cursor in ~/.cursor/mcp.json:',
                    signIn: 'Ein Client, der sich selbst anmelden kann (OAuth), braucht keinen Schlüssel und erstellt seine eigene Verbindung.',
                },
                signIn: {
                    name: 'Claude und ChatGPT',
                    hint: 'melden sich selbst an',
                    text: 'Claude (claude.ai, Desktop, App) und ChatGPT melden sich selbst bei Recap an und erstellen dabei ihre eigene Verbindung, brauchen diesen Schlüssel also nicht. Eine Anleitung findest du unter „Verbindung hinzufügen › KI-Assistenten (MCP)“.',
                },
            },
            renew: {
                title: 'Neuen Schlüssel erstellen?',
                confirm: 'Der bisherige Schlüssel funktioniert dann sofort nicht mehr.',
                submit: 'Neuen Schlüssel erstellen',
            },
        },
        caldav: {
            name: 'Kalender und Erinnerungen (CalDAV)',
            summary: 'Termine und To-dos in Apple Kalender, Erinnerungen und Thunderbird, zum Abhaken',
            description:
                'Ein Account für Apple Kalender und Erinnerungen, Thunderbird oder Android (mit DAVx⁵). Er enthält die Termine aus deinen veröffentlichten Recaps und alle deine To-dos. Wenn du ein To-do dort abhakst, ist es auch in Recap abgehakt.',
            password: {
                title: 'Dein App-Passwort',
                warning:
                    'Trage es gleich in deiner Kalender-App ein oder bewahre es in deinem Passwortmanager auf. Wer es zusammen mit deiner E-Mail-Adresse kennt, sieht deine Termine und To-dos. Wenn du es verlierst, erstelle ein neues.',
                copy: 'App-Passwort kopieren',
                saved: 'Ich habe das App-Passwort gespeichert',
            },
            details: {
                server: 'Server',
                serverHint: 'Für iPhone, iPad, Mac und Fantastical.',
                serverUrl: 'Vollständige Adresse',
                serverUrlHint: 'Für Thunderbird und Android.',
                username: 'Benutzername',
                password: 'App-Passwort',
                usedAt: 'Zuletzt verwendet',
                neverUsed: 'Noch nie',
            },
            setup: {
                title: 'Einrichten',
                lead: 'Füge in deiner Kalender-App einen CalDAV-Account hinzu, mit dem Server, deiner E-Mail-Adresse als Benutzername und dem App-Passwort. In der App kannst du To-dos abhaken und wieder öffnen. Was du dort sonst an einem To-do oder Termin änderst, setzt Recap auf den veröffentlichten Stand zurück.',
                iphone: {
                    name: 'iPhone und iPad',
                    step1: 'Öffne Einstellungen › Apps › Kalender › Kalender-Accounts › Account hinzufügen › Andere.',
                    step2: 'Tippe auf „CalDAV-Account hinzufügen“ und gib Server, Benutzername und App-Passwort ein.',
                    step3: 'Die Termine erscheinen danach in Kalender, die To-dos in Erinnerungen.',
                },
                mac: {
                    name: 'Mac',
                    step1: 'Öffne Kalender › Einstellungen › Accounts und klicke auf „+“ › „Anderer CalDAV-Account“.',
                    step2: 'Wähle „Manuell“ als Accounttyp und gib Benutzername, App-Passwort und Server ein.',
                },
                thunderbird: {
                    name: 'Thunderbird',
                    step1: 'Klicke im Kalender auf „Neuer Kalender“ › „Im Netzwerk“.',
                    step2: 'Gib den Benutzernamen und unter „Adresse“ die vollständige Adresse ein, dann das App-Passwort, wenn Thunderbird danach fragt.',
                },
                android: {
                    name: 'Android',
                    step1: 'Installiere DAVx⁵ und füge ein Konto mit „Mit URL und Benutzername anmelden“ hinzu, mit der vollständigen Adresse als URL.',
                    step2: 'Für die To-dos brauchst du außerdem eine Aufgaben-App wie jtx Board oder Tasks.org.',
                },
                others: 'Google Kalender und Outlook können keine CalDAV-Accounts hinzufügen. Verwende dort das Kalender-Abo.',
            },
            renew: {
                action: 'Neues App-Passwort erstellen',
                title: 'Neues App-Passwort erstellen?',
                confirm:
                    'Das bisherige App-Passwort funktioniert dann sofort nicht mehr. Trage das neue in jeder App ein, in der du den Account eingerichtet hast.',
            },
        },
        token: {
            warningTitle: 'Nur jetzt sichtbar',
            done: 'Fertig',
        },
        mcp: {
            name: 'KI-Assistenten (MCP)',
            description: 'Claude, ChatGPT, Claude Code und andere',
            title: 'Recap in KI-Assistenten nutzen',
            pick: 'Über MCP kann ein KI-Assistent mit deinen Rechten in deinem Workspace arbeiten. Wo möchtest du Recap nutzen?',
            clientTitle: 'Recap in {{client}}',
            address: 'MCP-Adresse',
            done: 'Fertig',
            signedIn:
                'Danach erscheint die Verbindung unter „Meine Verbindungen“, benannt nach dem Assistenten. Dort kannst du sie auch wieder trennen.',
            clients: {
                claude: { name: 'Claude', hint: 'claude.ai, Desktop, App' },
                chatgpt: { name: 'ChatGPT', hint: 'chatgpt.com, Desktop' },
                claudeCode: { name: 'Claude Code', hint: 'im Terminal' },
                other: { name: 'Andere', hint: 'Cursor, MCP Inspector …' },
            },
            claude: {
                open: 'Öffne in Claude:',
                path: 'Einstellungen › Konnektoren › Benutzerdefinierten Konnektor hinzufügen',
                url: 'Gib „Recap“ als Namen und diese URL ein:',
                signIn: 'Klicke auf „Verbinden“, melde dich bei Recap an, wähle den Workspace aus und erlaube den Zugriff.',
            },
            chatgpt: {
                open: 'Schalte in ChatGPT den Entwicklermodus ein:',
                path: 'Einstellungen › Apps & Konnektoren › Erweitert',
                url: 'Klicke auf „Erstellen“, gib „Recap“ als Namen ein, wähle „OAuth“ als Authentifizierung und gib diese URL ein:',
                signIn: 'Melde dich bei Recap an, wähle den Workspace aus und erlaube den Zugriff.',
            },
            claudeCode: {
                key: 'Claude Code meldet sich mit dem Schlüssel einer API-Verbindung an. Erstelle dafür eine:',
                create: 'API-Verbindung erstellen',
                existing: 'Du kannst auch den Schlüssel einer API-Verbindung verwenden, die du schon hast.',
                command: 'Im Terminal, mit deinem Schlüssel statt „rcp_…“:',
            },
            other: {
                url: 'Trage diese Adresse als MCP-Server über HTTP ein:',
                signIn: 'Wenn sich der Client anmelden kann (OAuth), leitet er dich zu Recap weiter. Andernfalls schickt er den Schlüssel einer API-Verbindung im Header „Authorization: Bearer rcp_…“, wie Claude Code.',
            },
        },
        forwarding: {
            howTo: 'Anleitung',
            setup: 'Weiterleitung einrichten',
            copyAddress: 'Adresse kopieren',
            name: 'E-Mail-Postfach (Weiterleitung)',
            summary: 'Ohne Passwort, mit Regeln in Recap',
            description:
                'Dein Postfach leitet alle E-Mails an eine Adresse von Recap weiter. Zu welchem Kunden und Projekt eine E-Mail gehört, entscheiden die Regeln hier, nicht Filter im Postfach. Ein Passwort braucht Recap dafür nicht.',
            ownNote:
                'Was dein Postfach an Recap weiterleitet, gehört dem Workspace: Alle, die den Kunden sehen, sehen auch diese E-Mails.',
            address: {
                hint: 'Zu welchem Kunden und Projekt eine weitergeleitete E-Mail gehört, entscheiden die Regeln unten.',
            },
            gmail: {
                title: 'Gmail',
                steps: {
                    add: 'Füge die Adresse oben unter <page>Einstellungen › Weiterleitung und POP/IMAP</page> als Weiterleitungsadresse hinzu.',
                    confirm:
                        'Bestätige mit dem Link, den Recap dir per E-Mail schickt, und leite dann eine Kopie eingehender E-Mails an die Adresse weiter.',
                },
            },
            outlook: {
                title: 'Outlook',
                steps: {
                    enable: 'Aktiviere in Outlook im Web unter Einstellungen › E-Mail › Weiterleitung die Weiterleitung an die Adresse oben.',
                },
                note: 'In vielen Microsoft-365-Organisationen ist die Weiterleitung nach außen gesperrt. Kommt nichts an, muss die IT sie für dein Postfach erlauben.',
            },
            feeds: {
                email: {
                    label: 'E-Mails empfangen',
                    description: 'Jede weitergeleitete E-Mail geht durch die Regeln dieses Postfachs.',
                },
            },
        },
        webhook: {
            description:
                'Für Make, Activepieces, n8n, Zapier und alles andere, was eine URL aufrufen kann: Daten, die an eine URL von Recap geschickt werden, werden zur Quelle, und veröffentlichte Recaps und ihre To-dos gehen an eine URL deiner Wahl.',
            inputUrl: 'Webhook-URL',
            inputUrlHint:
                'Schicke JSON per POST an diese URL, mit Text, Dateien als URLs oder beidem. Wer die URL kennt, kann Quellen hinzufügen. Um sie zu ersetzen, lösche den Feed und füge ihn neu hinzu.',
            inputPending: 'Sobald der Feed hinzugefügt ist, findest du die URL unter „…“ › Bearbeiten.',
            outputUrlHint:
                'Die URL, an die Recap JSON per POST schickt, zum Beispiel die URL eines „Custom Webhook“ in Make. Nur HTTPS.',
            signingSecret: 'Signaturschlüssel',
            signingSecretHint:
                'Jede Anfrage enthält den Header „X-Recap-Signature: sha256=…“: den HMAC-SHA256 ihres Bodys, signiert mit diesem Schlüssel.',
            example: 'Beispiel',
            report: {
                title: 'Aufgabe an Recap zurückmelden',
                hint: 'Macht dein Tool aus einem To-do eine Aufgabe, kann es sie mit einem Schlüssel der Verbindung „API (Entwickler)“ zurückmelden. Das To-do auf dem Recap verlinkt sie dann, und die nächsten Ereignisse zu diesem To-do enthalten ihre ID unter „external“.',
                key: '<API-Schlüssel>',
            },
            copyUrl: 'URL kopieren',
            fields: {
                url: 'URL',
                events: 'Ereignisse',
            },
            events: {
                todo: {
                    created: 'Erstellt: Ein To-do erscheint zum ersten Mal in einer veröffentlichten Version',
                    changed: 'Geändert: Eine neue Version ändert das To-do, oder es wird abgehakt oder wieder geöffnet',
                    removed: 'Entfernt: Das To-do ist in keiner veröffentlichten Version mehr enthalten',
                },
                recap: {
                    created: 'Erstellt: Ein Recap wird im Projekt erstellt, von Hand oder aus Quellen',
                    drafted: 'Entworfen: Der Entwurf eines Recaps ist fertig, auch wenn er neu entworfen wurde',
                    changed: 'Geändert: Der Entwurf eines Recaps wurde bearbeitet (höchstens alle 5 Minuten gesendet)',
                    published: 'Veröffentlicht: Eine Version wird veröffentlicht',
                    removed: 'Entfernt: Ein Recap wird gelöscht oder in ein anderes Projekt verschoben',
                },
            },
            feeds: {
                document: {
                    label: 'Quellen empfangen',
                    description: 'Daten, die dein Tool an eine URL von Recap schickt, werden zur Quelle.',
                },
                todos: {
                    label: 'To-dos senden',
                    description:
                        'Die To-dos aus den veröffentlichten Recaps eines Projekts gehen an eine URL deiner Wahl.',
                },
                recaps: {
                    label: 'Recaps senden',
                    description:
                        'Die Recaps eines Projekts gehen an eine URL deiner Wahl, wenn sie erstellt, entworfen, bearbeitet und veröffentlicht werden.',
                },
            },
        },
        ics: {
            description:
                'Eine Kalenderadresse für Google Kalender, Apple Kalender oder Outlook, die ohne Anmeldung funktioniert. Sie enthält die Termine aus veröffentlichten Recaps und auf Wunsch offene To-dos an ihrem Fälligkeitstag.',
            subscribe: 'Im Kalender abonnieren',
            everything: 'Alle Termine',
            address: 'Kalenderadresse',
            open: 'In Kalender öffnen',
            pending: 'Sobald das Abo hinzugefügt ist, findest du die Adresse unter „…“ › Bearbeiten.',
            ownTodos: 'Meine offenen To-dos am Fälligkeitstag eintragen',
            teamTodos: 'Offene To-dos am Fälligkeitstag eintragen, mit der zuständigen Person',
            howTo: {
                apple: 'Apple Kalender und Outlook am Computer: Klicke auf „In Kalender öffnen“.',
                google: 'Google Kalender: „Weitere Kalender“ › „Per URL“ und die Adresse einfügen. Es kann einige Stunden dauern, bis Google Änderungen anzeigt.',
                outlook:
                    'Outlook im Web: „Kalender hinzufügen“ › „Aus dem Internet abonnieren“ und die Adresse einfügen.',
            },
            secretNote:
                'Wer diese Adresse kennt, sieht alles, was der Kalender enthält. Gib sie nur an die Personen weiter, für die sie gedacht ist.',
            caldavHint:
                'Auf iPhone, Mac oder in Thunderbird kannst du deine To-dos auch als Erinnerungen sehen und dort abhaken.',
            caldavLink: 'CalDAV-Account hinzufügen',
            microsoftTodoHint: 'Mit Outlook kannst du deine To-dos als Aufgaben in Microsoft To Do abhaken.',
            microsoftTodoLink: 'Microsoft To Do verbinden',
            renew: 'Neue Adresse',
            renewTitle: 'Neue Adresse erstellen?',
            renewDescription:
                'Die bisherige Adresse funktioniert dann nicht mehr. Überall, wo sie abonniert ist, musst du die neue eintragen.',
            renewConfirm: 'Neue Adresse erstellen',
            feeds: {
                events: {
                    label: 'Termine im Kalender',
                    description:
                        'Die Termine aus veröffentlichten Recaps als Adresse, die jede Kalender-App abonnieren kann.',
                },
            },
        },
        imap: {
            name: 'E-Mail-Postfach',
            pickerName: 'E-Mail-Postfach (IMAP)',
            description:
                'Ein Postfach über IMAP. Recap liest E-Mails nur; es markiert sie nicht als gelesen und verschiebt sie nicht. Am besten eignet sich ein eigener Ordner oder eine eigene Adresse.',
            passwordHint: 'Leer lassen, um das gespeicherte Passwort zu behalten.',
            newPasswordHint: 'Verwende bei Gmail ein App-Passwort. Microsoft 365 wird noch nicht unterstützt.',
            folderHint: 'Meist INBOX oder ein Ordner, in den ein Filter die passenden E-Mails sortiert.',
            pickFolder: 'Ordner auswählen',
            foldersLoading: 'Ordner werden geladen …',
            ownNote:
                'Was Recap aus diesem Postfach liest, gehört dem Workspace: Alle, die den Kunden sehen, sehen auch diese E-Mails. Dein Passwort sieht niemand.',
            ownFolderHint:
                'Am besten ein eigener Ordner oder ein Label, in das ein Filter die passenden E-Mails sortiert, damit nichts Privates in den Workspace kommt.',
            providers: {
                placeholder: 'Anbieter auswählen',
                gmail: 'Gmail',
                outlook: 'Outlook / Microsoft 365',
                other: 'Anderer Anbieter',
            },
            gmail: {
                steps: {
                    verification:
                        'Aktiviere die <page>Bestätigung in zwei Schritten</page> in deinem Google-Konto, falls sie noch aus ist.',
                    appPassword:
                        'Erstelle unter <page>myaccount.google.com/apppasswords</page> ein App-Passwort, zum Beispiel mit dem Namen Recap.',
                    enter: 'Gib hier deine Gmail-Adresse und das App-Passwort ein. Die Leerzeichen kannst du mitkopieren.',
                },
                username: 'Gmail-Adresse',
                password: 'App-Passwort',
                passwordHint:
                    'Gibt es die Seite für App-Passwörter nicht, ist die Bestätigung in zwei Schritten aus oder dein Google-Workspace-Admin erlaubt keine App-Passwörter.',
                folderHint:
                    'In Gmail ist ein Ordner ein Label. Am besten legst du ein Label wie Recap an und lässt einen Filter die passenden E-Mails dort ablegen.',
            },
            fields: {
                provider: 'Anbieter',
                host: 'Server',
                port: 'Port',
                username: 'Benutzername',
                password: 'Passwort',
                folder: 'Ordner',
            },
            feeds: {
                email: {
                    label: 'E-Mails lesen',
                    description: 'Jede E-Mail in einem Ordner des Postfachs wird zu einer Quelle.',
                },
            },
        },
    },
    userSettings: {
        details: 'Angaben',
        editTitle: 'Profil bearbeiten',
        sso: {
            title: 'Anmeldung mit Google',
            hint: 'Du kannst dich mit deinem Google-Konto {{email}} anmelden.',
            disconnect: 'Trennen',
            disconnectTitle: 'Anmeldung mit Google trennen?',
            disconnectConfirm:
                'Danach meldest du dich mit einem Code an, den wir dir an deine E-Mail-Adresse schicken.',
        },
        deleteAccount: {
            action: 'Konto löschen',
            hint: 'Löscht dein Konto, deine Verbindungen und deine Einstellungen. Was du geschrieben, bestätigt und veröffentlicht hast, bleibt erhalten, aber ohne deinen Namen.',
            title: 'Konto löschen?',
            confirm:
                'Dein Konto, deine Verbindungen und deine Einstellungen werden gelöscht, und du verlässt alle Workspaces. Deine Kommentare, Bestätigungen und veröffentlichten Versionen bleiben erhalten und werden als „Gelöschtes Konto“ angezeigt. Das lässt sich nicht rückgängig machen.',
            email: 'E-Mail-Adresse',
            emailHint: 'Gib zur Bestätigung die E-Mail-Adresse deines Kontos ein.',
            submit: 'Konto löschen',
            ownsWorkspaces:
                'Dir gehören {{names}}. Übertrage oder lösche diese Workspaces, bevor du dein Konto löschst.',
        },
        leaveClient: {
            action: '{{client}} verlassen',
            hint: 'Du siehst die Recaps für {{client}} danach nicht mehr.',
            title: '{{client}} verlassen?',
            confirm:
                'Du siehst die Recaps für {{client}} in {{workspace}} danach nicht mehr. Zurück kommst du nur mit einer neuen Einladung.',
            submit: 'Verlassen',
        },
        fields: {
            firstName: 'Vorname',
            lastName: 'Nachname',
            displayName: 'Anzeigename',
            displayNameHelp: 'So sehen dich andere in deinen Workspaces.',
            jobTitle: 'Position',
            jobTitleHelp:
                'Optional, zum Beispiel „Projektleitung“. Steht neben deinem Namen, wo ein Recap dich erwähnt.',
            id: 'Nutzer-ID',
            avatar: 'Foto',
        },
        avatar: {
            choose: 'Foto auswählen',
            hint: 'PNG, JPEG oder WebP, bis 10 MB. Wird quadratisch aus der Mitte zugeschnitten.',
        },
        email: {
            title: 'E-Mail-Adresse',
            change: 'Ändern',
            enterCode: 'Code eingeben',
            current: 'Aktuelle Adresse',
            pending: 'Neue Adresse',
            pendingHint: 'Wartet auf Bestätigung',
            changeTitle: 'E-Mail-Adresse ändern',
            changeDescription:
                'Wir schicken einen Code an die neue Adresse. Bis du ihn eingibst, bleibt deine bisherige Adresse bestehen.',
            codeTitle: 'Code eingeben',
            submit: 'Bestätigen',
        },
        appearance: {
            title: 'Darstellung',
            general: 'Sprache und Modus',
            language: 'Sprache',
            mode: 'Modus',
            modes: {
                system: 'Systemeinstellung',
                light: 'Hell',
                dark: 'Dunkel',
            },
            themes: {
                title: 'Farben',
                hint: 'Wie Recap für dich aussieht. Nur du siehst es so, in jedem Browser, in dem du angemeldet bist.',
                classic: { name: 'Klassisch', description: 'Schwarz, Weiß und Grau, wie Recap bisher aussieht.' },
                terracotta: { name: 'Terrakotta', description: 'Terrakotta auf warmem Papierweiß.' },
                graphite: { name: 'Graphit', description: 'Graphit und Kupfer auf warmem Stein.' },
                navy: { name: 'Marine', description: 'Ein dunkles Marineblau auf kühlem Weiß.' },
                slate: { name: 'Schiefer', description: 'Ein gedecktes Blaugrau, am ruhigsten.' },
                indigo: { name: 'Indigo', description: 'Ein tiefes Indigo auf warmem Stein.' },
            },
            layouts: {
                title: 'Flächen',
                hint: 'Wie die Leiste links, die Leiste oben und die Seite zueinander stehen.',
                flat: { name: 'Flach', description: 'Alles eine Fläche, nur von Linien getrennt.' },
                tinted_rail: { name: 'Getönte Leiste', description: 'Die Leiste links einen Schritt dunkler.' },
                canvas: {
                    name: 'Leinwand',
                    description: 'Die Leisten hell, die Seite einen Schritt dunkler, die Karten darauf hell.',
                },
                inset: { name: 'Eingelassen', description: 'Die Seite als Blatt in der Fläche der Leiste.' },
                dark_frame: { name: 'Dunkler Rahmen', description: 'Die Leisten dunkel um die helle Seite.' },
            },
            fonts: {
                title: 'Schrift für Überschriften',
                sample: 'Guten Morgen',
                inter: 'Die Schrift der ganzen App, sachlich und klar.',
                fraunces: 'Eine weiche, warme Serifenschrift.',
                newsreader: 'Eine ruhige Serifenschrift zum Lesen.',
                hanken_grotesk: 'Eine warme, schlichte Grotesk.',
                bricolage_grotesque: 'Eine kräftige Grotesk mit Charakter.',
            },
            ownColors: {
                title: 'Eigene Farben',
                hint: 'Ändere jede Farbe einzeln, für hell und für dunkel. Eine Änderung gilt nur für diese eine Farbe.',
                light: 'Hell',
                dark: 'Dunkel',
                darkFollows: 'Dunkle Farben automatisch anpassen',
                groups: {
                    page: 'Seite und Karten',
                    text: 'Text',
                    primary: 'Hauptfarbe',
                    navigation: 'Navigation',
                    highlights: 'Hervorhebungen',
                },
                code: 'Als Code',
                codeHint:
                    'Dein ganzes Aussehen: Farben, Flächen, Schrift und jede Farbe hell und dunkel. Kopieren, um es mitzunehmen, oder ein anderes hineinkopieren, um es zu übernehmen.',
                codeInvalid:
                    'Das ist noch kein gültiger Code: theme, layout, headline_font, colors_light und colors_dark, jede Farbe als #rrggbb. Es gilt noch das letzte gültige Aussehen.',
                reset: 'Eigene Farben zurücksetzen',
                colors: {
                    primary: 'Hauptfarbe',
                    on_primary: 'Schrift darauf',
                    surface: 'Seite',
                    surface_shaded: 'Karten',
                    surface_muted: 'Grau',
                    border: 'Linien',
                    text: 'Text',
                    text_muted: 'Grauer Text',
                    rail: 'Leiste links',
                    top: 'Leiste oben',
                    chrome_dark: 'Dunkler Rahmen',
                    nav_hover: 'Eintrag unter der Maus',
                    nav_active: 'Aktiver Eintrag',
                    nav_active_text: 'Symbol darauf',
                    today: 'Heute',
                    today_text: 'Schrift auf Heute',
                    eyebrow: 'Datum oben',
                    tab_line: 'Tab-Strich',
                    bar_current: 'Heutiger Balken',
                    link: 'Links',
                },
            },
        },
        notifications: {
            title: 'E-Mail-Benachrichtigungen',
            hint: 'Wähle, worüber Recap dich per E-Mail informiert: sofort, im Tagesbericht um 8 Uhr oder nie. Einzelne Recaps kannst du über ihr Menü stummschalten.',
            sections: {
                recaps: 'Recaps',
                todos: 'To-dos',
                comments: 'Kommentare',
                acknowledgements: 'Bestätigungen',
                sources: 'Quellen',
                connections: 'Verbindungen',
            },
            deliveries: {
                immediately: 'Sofort',
                daily: 'Im Tagesbericht',
                never: 'Nie',
            },
            kinds: {
                recap_created: 'Ein Recap wurde erstellt',
                recap_drafted: 'Der Entwurf eines deiner Recaps ist fertig oder fehlgeschlagen',
                version_published: 'Eine neue Version wurde veröffentlicht',
                todo_ticked: 'Ein To-do in deinen Recaps wurde abgehakt oder wieder geöffnet',
                todo_due: 'Eines deiner To-dos ist morgen fällig oder war gestern fällig',
                comment_added: 'Jemand hat deine Recaps kommentiert oder dir geantwortet',
                acknowledgement_requested: 'Du wirst gebeten, einen Recap zu bestätigen, und Erinnerungen daran',
                recap_acknowledged: 'Jemand hat eine Version bestätigt, die du veröffentlicht hast',
                source_received: 'Eine Quelle ist eingegangen',
                source_failed: 'Eine Quelle konnte nicht gelesen werden',
                feed_failed: 'Eine Verbindung funktioniert nicht mehr',
            },
        },
    },
    clients: {
        tabs: {
            label: 'Bereiche',
            overview: 'Übersicht',
            team: 'Team',
            people: 'Personen',
            client: 'Kunde',
        },
        employeeCount_one: '{{count}} Person',
        employeeCount_other: '{{count}} Personen',
        employeeCountInvited_one: '{{count}} Person, {{invited}} eingeladen',
        employeeCountInvited_other: '{{count}} Personen, davon {{invited}} eingeladen',
        noEmployees: 'Noch keine Personen',
        filter: {
            search: 'Suchen',
            team: 'Team',
            all: 'Alle',
            mine: 'Meine',
        },
        noResults: {
            title: 'Keine Kunden gefunden',
            description: 'Kein Kunde passt zu deiner Suche und deinen Filtern.',
            descriptionSearch: 'Kein Kunde passt zu deiner Suche.',
            descriptionFilters: 'Kein Kunde passt zu deinen Filtern.',
            searchEverywhere: '„{{search}}“ in allen Kunden suchen',
            showFiltered_one: 'Alle Kunden mit diesem Filter zeigen',
            showFiltered_other: 'Alle Kunden mit diesen Filtern zeigen',
            showAll: 'Alle Kunden zeigen',
        },
        externalCompanies: {
            title: 'Aus {{provider}}',
            hint_one:
                'Ein Kunde aus {{provider}} ist noch nicht in Recap. Füge ihn hinzu oder verknüpfe ihn mit einem Kunden, den es schon gibt.',
            hint_other:
                '{{count}} Kunden aus {{provider}} sind noch nicht in Recap. Füge sie hinzu oder verknüpfe sie mit Kunden, die es schon gibt.',
            contactCount_one: 'Eine Ansprechperson',
            contactCount_other: '{{count}} Ansprechpersonen',
            add: 'Hinzufügen',
            open: 'In {{provider}} öffnen',
            dismiss: 'Ausblenden',
            linkedWith: 'Verknüpft mit',
            inProvider: '{{name}} in {{provider}}',
            link: {
                action: 'Mit Kunde verknüpfen',
                title: '{{name}} verknüpfen',
                hint: 'Was aus {{provider}} zu dieser Firma kommt, geht dann zu diesem Kunden.',
                submit: 'Verknüpfen',
            },
            unlink: {
                action: 'Verknüpfung lösen',
                title: 'Verknüpfung lösen?',
                confirm:
                    'Was aus {{provider}} zu {{name}} kommt, geht dann nicht mehr von selbst zu {{client}}. Unter „Kunden“ kannst du die Firma wieder verknüpfen.',
                submit: 'Verknüpfung lösen',
            },
        },
        team: {
            title: 'Team',
            hint: 'Wer {{name}} zugeordnet ist, bekommt seine neuen Quellen und Entwürfe.',
            assigned: {
                client: 'Dem Kunden zugeordnet sind',
                project: 'Dem Projekt zugeordnet sind',
            },
            scopeLabel: 'Wer zugeordnet ist',
            scopes: {
                workspace: 'alle von {{workspace}}',
                client: 'alle, die dem Kunden zugeordnet sind',
                chosen: 'nur ausgewählte',
            },
            details: {
                workspace_one: '{{count}} Person von {{workspace}} und wer später dazukommt',
                workspace_other: 'Alle {{count}} von {{workspace}} und wer später dazukommt',
                client_zero: 'Noch niemand von {{workspace}} ist {{client}} zugeordnet',
                client_one: '{{count}} Person von {{workspace}}, die {{client}} zugeordnet ist',
                client_other: '{{count}} von {{workspace}}, die {{client}} zugeordnet sind',
                chosen_one: '{{count}} von {{total}} aus {{workspace}}',
                chosen_other: '{{count}} von {{total}} aus {{workspace}}',
            },
            summary: {
                workspace: 'Ganzes Team',
                client: 'Team des Kunden',
                none: 'Noch niemand im Team',
            },
            add: 'Hinzufügen',
            remove: 'Aus dem Team entfernen',
            empty: {
                title: 'Noch niemand im Team',
                description: 'Füge über „Hinzufügen“ hinzu, wer an {{name}} arbeitet.',
            },
        },
        unfiledRecapCount_one: '{{count}} Recap ohne Projekt',
        unfiledRecapCount_other: '{{count}} Recaps ohne Projekt',
        unfiledSourceCount_one: '{{count}} Quelle ohne Projekt',
        unfiledSourceCount_other: '{{count}} Quellen ohne Projekt',
        drafting: {
            label: 'Wöchentlicher Entwurf',
            hint: 'Um 6 Uhr entwirft Recap einen Recap aus den wartenden Quellen, falls es welche gibt.',
            never: 'Nie',
            next: 'Aus diesen Quellen wird am {{weekday}} ein Recap entworfen.',
        },
        hasChanges: 'Ein Recap hier hat sich geändert, seit du ihn zuletzt geöffnet hast',
        star: {
            add: 'Zu Favoriten hinzufügen',
            remove: 'Aus Favoriten entfernen',
        },
        emptyPage: {
            title: 'Noch keine Kunden',
            description: 'Jeder Kunde hat seine Projekte, und darin liegen die Recaps, die du mit ihm teilst.',
        },
        new: 'Kunde erstellen',
        newTitle: 'Neuer Kunde',
        editTitle: 'Kunde bearbeiten',
        field: 'Kunde',
        none: 'Kein Kunde',
        fields: {
            name: 'Name',
            slug: 'Kürzel',
            slugHint: 'Erscheint in der Adresse der Seite: <path />',
            id: 'Kunden-ID',
            address: 'Adresse',
            description: 'Beschreibung',
            logo: 'Logo',
        },
        logo: {
            choose: 'Logo auswählen',
            hint: 'PNG, JPEG oder WebP, bis 2 MB.',
        },
        sections: {
            projects: 'Projekte',
            unfiledSources: 'Quellen ohne Projekt',
            unfiledRecaps: 'Recaps ohne Projekt',
            details: 'Angaben',
        },
        access: {
            sharedWith: 'Freigegeben für',
            toClient: 'Zum Kunden',
            title: 'Personen von {{client}}',
            hint: 'Sie lesen die veröffentlichten Recaps von {{project}} und bestätigen sie. Entwürfe, Quellen und Kommentare deines Teams sehen sie nie.',
            sentence: 'Mitlesen dürfen',
            label: 'Wer mitliest',
            all: 'alle von {{client}}',
            chosen: 'nur ausgewählte',
            details: {
                all_one: '{{count}} Person von {{client}} und wer später eingeladen wird',
                all_other: 'Alle {{count}} von {{client}} und wer später eingeladen wird',
                chosen_one: '{{count}} von {{total}} aus {{client}}',
                chosen_other: '{{count}} von {{total}} aus {{client}}',
            },
            invite: 'Person von {{client}} einladen',
            remove: 'Nicht mehr mitlesen lassen',
            empty: {
                title: 'Noch liest niemand von {{client}} mit',
                description: 'Füge über „Hinzufügen“ hinzu, wer die Recaps lesen soll.',
            },
            noPeople: '{{client}} hat noch keine Personen',
            noPeopleHint: 'Lade ein, wer die Recaps lesen und bestätigen soll.',
            choose: 'Kunde wählen',
            otherClient: 'Kunde wechseln',
            removeClient: 'Entfernen',
            newClient: 'Neuen Kunden erstellen',
            shareTitle: 'Für einen Kunden freigeben',
            shareHint:
                'Gib {{project}} für einen Kunden frei, damit seine Personen die Recaps lesen und bestätigen. Ohne Kunden bleibt es ein internes Projekt für dein Team.',
            peopleOfClient: 'Personen des Kunden',
            peopleOfClientHint: 'Hier legst du fest, wer vom Kunden mitliest: alle oder nur ausgewählte.',
            noClientHint: 'Sobald du einen Kunden wählst, legst du hier fest, wer von ihm die Recaps liest.',
            move: {
                title: 'Für {{client}} freigeben?',
                confirm:
                    '{{project}} wird mit seinen Recaps und Quellen für {{to}} freigegeben. Die Personen von {{from}} lesen es dann nicht mehr und werden nicht mehr gebeten, seine Recaps zu bestätigen.',
                submit: 'Freigeben',
            },
            takeAway: {
                title: '{{client}} entfernen?',
                confirm:
                    'Die Personen von {{client}} lesen {{project}} dann nicht mehr und werden nicht mehr gebeten, seine Recaps zu bestätigen. Das Projekt ist dann nur für dein Team.',
                submit: 'Entfernen',
            },
        },
        employees: {
            title: 'Personen von {{client}}',
            hint: 'Sie lesen die veröffentlichten Recaps von {{client}} und bestätigen sie. Wer welches Projekt liest, legst du am Projekt im Reiter „Kunde“ fest.',
            invitedFor: 'Für {{projects}} eingeladen',
            invite: {
                action: 'Person einladen',
                title: 'Person von {{name}} einladen',
                hint: 'Wir schicken eine Einladung an diese Adresse. Beitreten kann nur, wer mit dieser Adresse angemeldet ist.',
            },
            contacts: {
                title: 'Ansprechpersonen aus {{provider}}',
                invite: 'Einladen',
            },
            link: {
                none: 'Wer den Link hat, kann mit dem eigenen Konto beitreten.',
            },
            projects: {
                all: 'Liest alle Projekte',
                some: 'Liest {{projects}}',
                empty: 'Liest noch kein Projekt',
            },
            remove: {
                action: 'Entfernen',
                title: 'Person entfernen?',
                confirm: '{{name}} gehört danach nicht mehr zu den Personen von {{client}}.',
                submit: 'Entfernen',
            },
        },
        allSources_one: 'Quelle ansehen',
        allSources_other: 'Alle {{count}} ansehen',
        allRecaps_one: 'Recap ansehen',
        allRecaps_other: 'Alle {{count}} ansehen',
        noUnfiledSources: {
            description: 'Hier stehen die Quellen von {{client}}, die noch keinem Projekt zugeordnet sind.',
        },
        unfiledNeedProject: 'Erstelle ein Projekt, um die Quellen dieses Kunden zuzuordnen.',
        delete: {
            action: 'Kunde löschen',
            hint: 'Löscht alle seine Projekte, Personen und Einladungen. Seine Quellen bleiben erhalten, aber ohne Kunde.',
            title: 'Kunde löschen?',
            confirm:
                '{{name}} wird mit allen Projekten gelöscht. Seine Quellen bleiben erhalten, aber ohne Kunde. Das lässt sich nicht rückgängig machen.',
            submit: 'Löschen',
            blockedTitle: 'Kunde kann nicht gelöscht werden',
            blocked:
                'Die Projekte von {{name}} enthalten Recaps. Verschiebe oder lösche sie zuerst – sie halten fest, was vereinbart wurde.',
        },
        projects: {
            count_one: '{{count}} Projekt',
            count_other: '{{count}} Projekte',
            empty: 'Noch keine Projekte.',
            new: 'Projekt erstellen',
            newTitle: 'Neues Projekt',
            newHint:
                'In einem Projekt liegen die Quellen und Recaps einer Arbeit. Mit einem Kunden lesen seine Personen die Recaps, ohne Kunden bleibt es ein internes Projekt für dein Team.',
            withoutClient: 'Ohne Kunde',
            placeholder: {
                title: 'Ohne Projekt',
                create: 'Erstes Projekt erstellen',
            },
            noResults: {
                title: 'Keine Projekte gefunden',
                description: 'Kein Projekt passt zu deiner Suche und deinen Filtern.',
                descriptionSearch: 'Kein Projekt passt zu deiner Suche.',
                descriptionFilters: 'Kein Projekt passt zu deinen Filtern.',
                searchEverywhere: '„{{search}}“ in allen Projekten suchen',
                showFiltered_one: 'Alle Projekte mit diesem Filter zeigen',
                showFiltered_other: 'Alle Projekte mit diesen Filtern zeigen',
                showAll: 'Alle Projekte zeigen',
            },
            page: {
                empty: {
                    title: 'Noch keine Projekte',
                    description:
                        'In einem Projekt liegen die Quellen und Recaps einer Arbeit, für einen Kunden oder nur für dein Team.',
                },
            },
            editTitle: 'Projekt bearbeiten',
            field: 'Projekt',
            none: 'Kein Projekt',
            pick: 'Projekt auswählen',
            waitingSources_one: '{{count}} neue Quelle',
            waitingSources_other: '{{count}} neue Quellen',
            noRecaps: 'Noch kein Recap',
            lastRecap: 'zuletzt {{date}}',
            weeklyDraft: 'Entwurf jeden {{weekday}}',
            newSources: 'Neue Quellen',
            newSourcesEmpty: {
                description: 'Hier stehen die Quellen von „{{project}}“, die noch in keinem Recap sind.',
            },
            emptySection: {
                description: 'In Projekten liegen die Recaps von {{client}}, z. B. einer pro Auftrag.',
            },
            recapsEmpty: {
                description: 'Lade Dateien zu diesem Projekt hoch. Daraus entsteht ein Recap.',
            },
            notPublic: 'Nicht öffentlich',
            publicLink: {
                title: 'Öffentlicher Link',
                hint: 'Wer den Link hat, kann die veröffentlichten Recaps von {{project}} ohne Konto lesen. Kommentare, Bestätigungen und ältere Versionen sind dabei nicht sichtbar.',
                create: 'Link erstellen',
                renew: 'Neuen Link erstellen',
                turnOff: 'Link deaktivieren',
            },
            fields: {
                id: 'Projekt-ID',
                name: 'Name',
                client: 'Kunde',
            },
            delete: {
                action: 'Projekt löschen',
                hint: 'Seine Quellen bleiben erhalten, aber ohne Projekt.',
                button: 'Löschen',
                title: 'Projekt löschen?',
                confirm: '{{name}} wird gelöscht. Das lässt sich nicht rückgängig machen.',
                submit: 'Löschen',
                blockedTitle: 'Projekt kann nicht gelöscht werden',
                blocked:
                    '{{name}} enthält Recaps. Verschiebe oder lösche sie zuerst – sie halten fest, was vereinbart wurde.',
            },
        },
    },
    employee: {
        team: {
            title: 'Dein Team bei {{workspace}}',
        },
        emptySection: {
            title: 'Noch keine Recaps',
            project: 'Sobald ein Recap zu diesem Projekt veröffentlicht ist, steht er hier.',
            all: 'Sobald ein Recap für dich veröffentlicht ist, steht er hier.',
            notifications: 'Benachrichtigungen einstellen',
        },
        home: {
            intro: 'Hier hält {{workspace}} fest, was mit {{client}} besprochen und entschieden wurde.',
            waitingTitle: 'Wartet auf deine Bestätigung',
            allRecaps_one: 'Recap ansehen',
            allRecaps_other: 'Alle {{count}} ansehen',
        },
        acknowledge: {
            title: 'Bitte bestätige Version {{number}}',
            description: 'Mit deiner Bestätigung erklärst du, dass der Recap festhält, was vereinbart wurde.',
            changedSince: 'Geändert seit deiner Bestätigung von Version {{number}}:',
            button: 'Bestätigen',
            done: 'Von dir bestätigt am {{date}}',
        },
        recap: {
            published: 'Veröffentlicht am {{date}}',
            viewLatest: 'Neueste Version ansehen',
            olderVersion: 'Die neueste Version ist Version {{number}}.',
        },
    },
    home: {
        greeting: {
            morning: 'Guten Morgen, {{name}}',
            day: 'Hallo, {{name}}',
            evening: 'Guten Abend, {{name}}',
        },
        overdueSince: 'überfällig seit {{date}}',
        allDay: 'ganztägig',
        summary: {
            overdue_one: '{{count}} To-do ist überfällig',
            overdue_other: '{{count}} To-dos sind überfällig',
            dueTodayOne: '„{{title}}“ ist heute fällig',
            dueToday_one: '{{count}} To-do ist heute fällig',
            dueToday_other: '{{count}} To-dos sind heute fällig',
            owed: '{{name}} schuldet dir „{{title}}“ bis {{date}}',
            owedLate: '{{name}} ist mit „{{title}}“ im Verzug',
            event: '„{{title}}“ ist {{when}}',
            atTime: 'heute um {{time}}',
            onDay: 'am {{date}}',
            calm: 'Heute steht nichts an. Ein guter Moment, um neue Quellen durchzusehen.',
        },
        stats: {
            open: 'Offene To-dos',
            overdue_one: 'davon {{count}} überfällig',
            overdue_other: 'davon {{count}} überfällig',
            nothingOverdue: 'nichts überfällig',
            done: 'Diese Woche erledigt',
            doneDetail: 'To-dos seit Montag',
            doneTrend: 'Erledigte To-dos der letzten 7 Tage: {{values}}',
            received: 'Neue Quellen',
            receivedDetail: 'in den letzten 7 Tagen',
            receivedTrend: 'Eingegangene Quellen der letzten 7 Tage: {{values}}',
            events: 'Termine diese Woche',
            nextEvent: 'nächster am {{date}}',
            noEvents: 'keine mehr diese Woche',
        },
        week: {
            title: 'Diese Woche',
            nextUp: 'Diese Woche ist frei. Als Nächstes:',
            nothingNext: 'Keine weiteren Termine',
            nothingNextHint: 'Termine und fällige To-dos aus den Recaps stehen hier, sobald es welche gibt.',
            itemCount_one: '{{count}} Eintrag',
            itemCount_other: '{{count}} Einträge',
        },
        myTodos: {
            title: 'Deine To-dos',
            done: 'Erledigt',
            undo: 'Rückgängig',
            allDone: 'Alles erledigt',
            allDoneHint: 'Du hast keine offenen To-dos.',
        },
        news: {
            recapAcknowledged: '{{user}} hat Version {{number}} von „{{recap}}“ bestätigt',
            acknowledgerRemoved: '{{name}} wird bei „{{recap}}“ nicht mehr um Bestätigung gebeten',
            recapProcessedAgain: '{{user}} hat „{{recap}}“ neu entworfen',
            recapSourcesChanged: '{{user}} hat die Quellen von „{{recap}}“ geändert',
            draftDiscarded: '{{user}} hat den Entwurf von „{{recap}}“ verworfen',
            versionRestored: '{{user}} hat Version {{number}} von „{{recap}}“ wiederhergestellt',
            recapMoved: '{{user}} hat „{{recap}}“ verschoben',
            sourceMoved: '{{user}} hat eine Quelle verschoben',
            publicLinkCreated: '{{user}} hat einen öffentlichen Link zu „{{recap}}“ erstellt',
            publicLinkRemoved: '{{user}} hat den öffentlichen Link zu „{{recap}}“ entfernt',
            more_one: '{{count}} weitere unter „Aktivitäten“',
            more_other: '{{count}} weitere unter „Aktivitäten“',
            all: 'Alle Aktivitäten',
            sources_one: 'Neue Quelle von {{client}}',
            sources_other: '{{count}} neue Quellen von {{client}}',
            sourcesWithoutClient_one: 'Neue Quelle ohne Kunde',
            sourcesWithoutClient_other: '{{count}} neue Quellen ohne Kunde',
            sourcesOfProject_one: 'Neue Quelle in „{{project}}“',
            sourcesOfProject_other: '{{count}} neue Quellen in „{{project}}“',
            sourceFailed: 'Eine Quelle konnte nicht gelesen werden',
            sourceUpdated: '{{user}} hat eine Quelle aktualisiert',
            sourceProcessedAgain: '{{user}} hat eine Quelle neu verarbeitet',
            sourceChanged: 'Für eine Quelle gibt es eine neuere Version',
            recapCreated: '{{user}} hat „{{recap}}“ erstellt',
            recapDrafted: 'Der Entwurf von „{{recap}}“ ist fertig',
            recapFailed: 'Der Entwurf von „{{recap}}“ ist fehlgeschlagen',
            versionPublished: '{{user}} hat Version {{number}} von „{{recap}}“ veröffentlicht',
            todoDone: '{{user}} hat „{{title}}“ abgehakt',
            todoUndone: '{{user}} hat „{{title}}“ wieder geöffnet',
            todos: '{{user}} hat {{count}} To-dos in „{{recap}}“ abgehakt',
            comments_one: 'Neuer Kommentar zu „{{recap}}“',
            comments_other: '{{count}} neue Kommentare zu „{{recap}}“',
            sourceComments_one: 'Neuer Kommentar zu einer Quelle',
            sourceComments_other: '{{count}} neue Kommentare zu Quellen',
        },
        clients: {
            title: 'Deine Kunden',
            todosTitle_one: '{{count}} offenes To-do',
            todosTitle_other: '{{count}} offene To-dos',
            noTodos: 'Keine offenen To-dos',
            moreTodos_one: '{{count}} weiteres auf der Seite des Kunden',
            moreTodos_other: '{{count}} weitere auf der Seite des Kunden',
            eventsTitle: 'Nächste Termine',
            health: {
                overdue: 'Überfällig',
                needs_recap: 'Neue Quellen',
            },
            counts: {
                sources_one: '{{count}} Quelle wartet auf einen Recap',
                sources_other: '{{count}} Quellen warten auf einen Recap',
                todos_one: '{{count}} offenes To-do',
                todos_other: '{{count}} offene To-dos',
                events_one: '{{count}} anstehender Termin',
                events_other: '{{count}} anstehende Termine',
            },
            noEvent: 'Kein Termin geplant',
            createRecap_one: 'Recap aus {{count}} Quelle erstellen',
            createRecap_other: 'Recap aus {{count}} Quellen erstellen',
            waitingSources_one: '{{count}} Quelle wartet auf einen Recap.',
            waitingSources_other: '{{count}} Quellen warten auf einen Recap.',
            toggle: '{{name}} ein- oder ausklappen',
        },
        projects: {
            title: 'Interne Projekte',
            moreTodos_one: '{{count}} weiteres auf der Seite des Projekts',
            moreTodos_other: '{{count}} weitere auf der Seite des Projekts',
        },
    },
    activities: {
        markAllSeen: 'Alle als gesehen markieren',
        automatic: {
            on_arrival: 'Recap automatisch aus einer neuen Quelle erstellt',
            weekly: 'Wöchentlicher Recap automatisch erstellt',
        },
        newForYou: 'Neu für dich',
        none: 'Bisher ist nichts passiert',
        emptyPage: {
            title: 'Bisher ist nichts passiert',
            description:
                'Hier siehst du, was in den Recaps passiert: neue Entwürfe, Kommentare und veröffentlichte Versionen.',
            toSources: 'Zu den Quellen',
        },
        deletedRecap: 'Gelöschter Recap',
        deletedSource: 'Gelöschte Quelle',
        noClient: 'ohne Kunde',
        someone: 'Jemand',
        you: 'Du',
        group: { expand: 'Einzeln anzeigen', collapse: 'Zusammenfassen' },
        details: { expand: 'Details anzeigen', collapse: 'Details ausblenden' },
        published: 'hat Version {{number}} veröffentlicht',
        published_you: 'hast Version {{number}} veröffentlicht',
        acknowledgementRequested: 'Neue Bestätigung angefordert',
        changes: {
            kinds: {
                source_file: 'Datei',
                person: 'Person',
                topic: 'Thema',
                decision: 'Entscheidung',
                todo: 'To-do',
                event: 'Termin',
            },
            more_one: '{{count}} weitere Änderung',
            more_other: '{{count}} weitere Änderungen',
        },
        drafts: {
            drafted: 'Entwurf erstellt',
            draftedAgain_one: 'Neu entworfen',
            draftedAgain_other: '{{count}}-mal neu entworfen',
            createdAndDrafted_one: 'hat einen Entwurf erstellt',
            createdAndDrafted_you_one: 'hast einen Entwurf erstellt',
            createdAndDrafted_other: 'hat {{count}} Entwürfe erstellt',
            createdAndDrafted_you_other: 'hast {{count}} Entwürfe erstellt',
        },
        todos: {
            done: 'hat „{{title}}“ abgehakt',
            done_you: 'hast „{{title}}“ abgehakt',
            itemDone: '„{{title}}“ abgehakt',
            entryDone: 'Abgehakt',
            entryUndone: 'Wieder geöffnet',
            itemUndone: '„{{title}}“ wieder geöffnet',
            undone: 'hat „{{title}}“ wieder geöffnet',
            undone_you: 'hast „{{title}}“ wieder geöffnet',
            doneAndUndone: 'hat „{{title}}“ abgehakt und wieder geöffnet',
            doneAndUndone_you: 'hast „{{title}}“ abgehakt und wieder geöffnet',
            undoneAndDone: 'hat „{{title}}“ wieder geöffnet und erneut abgehakt',
            undoneAndDone_you: 'hast „{{title}}“ wieder geöffnet und erneut abgehakt',
            backAndForthDone: 'hat „{{title}}“ mehrmals abgehakt und wieder geöffnet und schließlich erledigt',
            backAndForthDone_you: 'hast „{{title}}“ mehrmals abgehakt und wieder geöffnet und schließlich erledigt',
            backAndForthUndone: 'hat „{{title}}“ mehrmals abgehakt und wieder geöffnet und offen gelassen',
            backAndForthUndone_you: 'hast „{{title}}“ mehrmals abgehakt und wieder geöffnet und offen gelassen',
            severalDone_one: 'hat {{count}} To-do abgehakt',
            severalDone_you_one: 'hast {{count}} To-do abgehakt',
            severalDone_other: 'hat {{count}} To-dos abgehakt',
            severalDone_you_other: 'hast {{count}} To-dos abgehakt',
            severalUndone_one: 'hat {{count}} To-do wieder geöffnet',
            severalUndone_you_one: 'hast {{count}} To-do wieder geöffnet',
            severalUndone_other: 'hat {{count}} To-dos wieder geöffnet',
            severalUndone_you_other: 'hast {{count}} To-dos wieder geöffnet',
            severalMixed_one: 'hat {{count}} To-do abgehakt und {{undone}} wieder geöffnet',
            severalMixed_you_one: 'hast {{count}} To-do abgehakt und {{undone}} wieder geöffnet',
            severalMixed_other: 'hat {{count}} To-dos abgehakt und {{undone}} wieder geöffnet',
            severalMixed_you_other: 'hast {{count}} To-dos abgehakt und {{undone}} wieder geöffnet',
        },
        sources: {
            added_one: 'hat {{count}} Quelle hinzugefügt',
            added_you_one: 'hast {{count}} Quelle hinzugefügt',
            added_other: 'hat {{count}} Quellen hinzugefügt',
            added_you_other: 'hast {{count}} Quellen hinzugefügt',
            synced_one: '{{count}} E-Mail ist eingegangen',
            synced_other: '{{count}} E-Mails sind eingegangen',
        },
        commentOn: {
            topic: 'Zum Thema „{{title}}“',
            decision: 'Zur Entscheidung „{{title}}“',
            todo: 'Zum To-do „{{title}}“',
            event: 'Zum Termin „{{title}}“',
        },
        kinds: {
            source_received: 'Quelle hinzugefügt',
            source_failed: 'Verarbeitung fehlgeschlagen',
            source_updated: 'Quelle aktualisiert',
            source_processed_again: 'Neu verarbeitet',
            source_changed: 'Neuere Version verfügbar',
            recap_created: 'Recap erstellt',
            recap_drafted: 'Entwurf erstellt',
            recap_failed: 'Entwurf fehlgeschlagen',
            version_published: 'Version {{number}} veröffentlicht',
            todo_done: 'Erledigt: {{title}}',
            todo_undone: 'Wieder geöffnet: {{title}}',
            item_superseded: 'Durch einen neueren Recap ersetzt: {{title}}',
            comment_added: 'Kommentiert',
            recap_acknowledged: 'Version {{number}} bestätigt',
            acknowledger_removed:
                '{{name}} hat keinen Zugriff mehr auf das Projekt und wird nicht mehr um Bestätigung gebeten',
            recap_processed_again: 'Neu entworfen',
            recap_sources_changed: 'Quellen geändert',
            draft_discarded: 'Entwurf verworfen',
            version_restored: 'Version {{number}} wiederhergestellt',
            recap_moved: 'Verschoben nach {{place}}',
            source_moved: 'Verschoben nach {{place}}',
            public_link_created: 'Öffentlichen Link erstellt',
            public_link_removed: 'Öffentlichen Link entfernt',
            recap_deleted: 'Recap gelöscht',
            source_deleted: 'Quelle gelöscht',
            client_created: 'Kunde erstellt',
            client_renamed: 'Umbenannt (vorher: „{{title}}“)',
            client_deleted: 'Kunde gelöscht',
            project_created: 'Projekt erstellt',
            project_renamed: 'Umbenannt (vorher: „{{title}}“)',
            project_deleted: 'Projekt gelöscht',
            project_client_changed: 'Kunde geändert',
        },
    },
    comments: {
        isNew: 'Neu, seit du den Recap zuletzt geöffnet hast',
        title: 'Kommentare',
        show_one: '{{count}} Kommentar',
        show_other: '{{count}} Kommentare',
        add: 'Kommentieren',
        empty: 'Noch keine Kommentare',
        placeholder: 'Kommentar schreiben …',
        submit: 'Kommentieren',
        onDraftNote: 'Du kommentierst den Entwurf von Version {{number}}. Nur Mitglieder sehen diesen Kommentar.',
        edited: 'bearbeitet',
        onVersion: 'Zu Version {{number}}',
        onDraft: 'Zum Entwurf von Version {{number}}',
        onItem: {
            topic: 'Zum Thema „{{title}}“',
            decision: 'Zur Entscheidung „{{title}}“',
            todo: 'Zum To-do „{{title}}“',
            event: 'Zum Termin „{{title}}“',
        },
        onUnknownItem: {
            topic: 'Zu einem Thema',
            decision: 'Zu einer Entscheidung',
            todo: 'Zu einem To-do',
            event: 'Zu einem Termin',
        },
        deletedUser: 'Gelöschtes Konto',
        reply: 'Antworten',
        replyingTo: 'Antwort an {{name}}',
        stopReplying: 'Antwort abbrechen',
        onComment: 'Zum Kommentar „{{text}}“',
        onDeletedComment: 'Zu einem gelöschten Kommentar',
        delete: {
            title: 'Kommentar löschen?',
            confirm: 'Das lässt sich nicht rückgängig machen.',
            submit: 'Löschen',
        },
    },
    recaps: {
        hasChanges: 'Geändert, seit du ihn zuletzt geöffnet hast',
        itemChanged: 'Neu oder geändert, seit du den Recap zuletzt geöffnet hast',
        activity: {
            title: 'Aktivität',
            onlyComments: 'Nur Kommentare',
            order: 'Reihenfolge',
            newest: 'Neueste zuerst',
            oldest: 'Älteste zuerst',
        },
        resultCount_one: '{{count}} Recap',
        resultCount_other: '{{count}} Recaps',
        emptyPage: {
            title: 'Noch keine Recaps',
            description:
                'Lade E-Mails, Meeting-Notizen oder Dokumente hoch. Daraus entsteht ein Recap. Du kannst einen Recap auch <create>selbst schreiben</create>.',
        },
        upload: {
            description:
                'Wähle den Kunden und das Projekt, zu denen die Dateien gehören. Aus ihnen entsteht dann ein Recap.',
        },
        noResults: {
            title: 'Keine Recaps gefunden',
            description: 'Kein Recap passt zu deiner Suche und deinen Filtern.',
            descriptionSearch: 'Kein Recap passt zu deiner Suche.',
            descriptionFilters: 'Kein Recap passt zu deinen Filtern.',
            searchEverywhere: '„{{search}}“ in allen Recaps suchen',
            showFiltered_one: 'Alle Recaps mit diesem Filter zeigen',
            showFiltered_other: 'Alle Recaps mit diesen Filtern zeigen',
            showAll: 'Alle Recaps zeigen',
        },
        draftsTitle: 'Entwürfe',
        processingTitle: 'Wird verarbeitet …',
        untitled: 'Ohne Titel',
        failedTitle: 'Die Verarbeitung ist fehlgeschlagen',
        failed: 'Du kannst sie unter „Weitere Aktionen“ neu starten.',
        noSummary: 'Keine Zusammenfassung gefunden',
        processAgain: 'Neu verarbeiten',
        publicLink: {
            hint: 'Wer den Link hat, kann die veröffentlichte Version dieses Recaps ohne Konto lesen, aber nichts anderes aus dem Projekt. Kommentare, Bestätigungen und ältere Versionen sind dabei nicht sichtbar.',
            notPublished:
                'Nur veröffentlichte Recaps können geteilt werden. Sobald dieser veröffentlicht ist, kannst du hier einen Link erstellen.',
        },
        mute: {
            mute: 'Benachrichtigungen stummschalten',
            unmute: 'Benachrichtigungen einschalten',
            muted: 'Stummgeschaltet: Du bekommst zu diesem Recap keine E-Mails.',
        },
        editing: {
            title: 'Du bearbeitest Version {{number}}',
            published:
                'Version {{number}} wurde zuletzt am {{date}} veröffentlicht. Bis du veröffentlichst, sehen nur Mitglieder deine Änderungen.',
            notPublished: 'Noch nicht veröffentlicht. Bis du ihn veröffentlichst, sehen ihn nur Mitglieder.',
            close: 'Bearbeiten beenden',
        },
        sections: {
            summary: 'Zusammenfassung',
            topics: 'Themen',
            persons: 'Personen',
            sources: 'Quellen',
            decisions: 'Entscheidungen',
            todos: 'To-dos',
            events: 'Termine',
        },
        items: {
            topic: 'Thema',
            decision: 'Entscheidung',
            todo: 'To-do',
            todoDone: 'To-do, erledigt',
            supersedes: 'Ersetzt „{{title}}“ aus <recap>„{{recap}}“</recap>',
            supersedesFrom: 'Ersetzt einen Eintrag aus <recap>„{{recap}}“</recap>',
            openExternal: 'In {{provider}} öffnen',
            openExternalUnnamed: 'Im Tool öffnen',
            supersededBy: 'Ersetzt durch „{{title}}“ in <recap>„{{recap}}“</recap>',
            supersededIn: 'Ersetzt in <recap>„{{recap}}“</recap>',
            event: 'Termin',
        },
        todoDue: 'Fällig am {{date}}',
        allDay: 'Ganztägig',
        linkedPerson: 'Mit einem Konto verknüpft',
        counts: {
            persons_one: '{{count}} Person',
            persons_other: '{{count}} Personen',
            topics_one: '{{count}} Thema',
            topics_other: '{{count}} Themen',
            decisions_one: '{{count}} Entscheidung',
            decisions_other: '{{count}} Entscheidungen',
            todos_one: '{{count}} To-do',
            todos_other: '{{count}} To-dos',
            openForYou_one: 'davon {{count}} offen für dich',
            openForYou_other: 'davon {{count}} offen für dich',
            events_one: '{{count}} Termin',
            events_other: '{{count}} Termine',
        },
        persons: {
            participants: 'Teilnehmende',
            mentioned: 'Erwähnt',
        },
        citations: {
            show_one: '{{count}} Fundstelle',
            show_other: '{{count}} Fundstellen',
            title: 'Fundstellen',
            sourceDeleted: 'Quelle gelöscht',
        },
        create: {
            button: 'Recap erstellen',
            title: 'Recap erstellen',
            description: 'Der Recap entsteht aus diesen Dateien. Abgewählte Dateien werden nicht berücksichtigt.',
            submit: 'Erstellen',
        },
        sourceCount_one: '{{count}} Quelle',
        sourceCount_other: '{{count}} Quellen',
        new: {
            button: 'Recap erstellen',
            title: 'Recap erstellen',
            description:
                'Ein Recap, den du selbst ausfüllst. Um einen aus Quellen entwerfen zu lassen, wähle sie auf der Seite „Quellen“ aus.',
        },
        project: {
            button: 'Kunde und Projekt ändern',
            title: 'Kunde und Projekt',
            description: 'Zu welchem Kunden und Projekt gehört dieser Recap? Sein Inhalt bleibt unverändert.',
        },
        draftOfNextVersionTitle: 'Entwurf von Version {{number}}',
        draftOfNextVersion: 'Nur Mitglieder sehen ihn. Veröffentlicht ist Version {{published}}.',
        notPublishedTitle: 'Entwurf',
        notPublished: 'Noch nicht veröffentlicht. Nur Mitglieder sehen ihn.',
        hasDraft: {
            title: 'Es gibt einen Entwurf von Version {{number}}',
            description: 'Bis er veröffentlicht ist, sehen ihn nur Mitglieder.',
        },
        drafting: {
            title: 'Version {{number}} wird gerade entworfen',
            description: 'Bis der Entwurf fertig ist, siehst du hier die veröffentlichte Version.',
        },
        publishedVersion: 'Version {{number}} · veröffentlicht am {{date}}',
        draftedAt: 'Entworfen am {{date}}',
        status: {
            draft: 'Entwurf',
            version: 'Version {{number}}',
            versionPublished: 'Version {{number}} veröffentlicht',
        },
        // What a row's icon says (RecapStateIcon).
        state: {
            legend: 'Was die Symbole bedeuten',
            processing: 'Wird entworfen',
            failed: 'Fehlgeschlagen',
            draft: 'Entwurf',
            newDraft: 'Neuer Entwurf',
            waiting: 'Wartet auf Bestätigungen',
            published: 'Veröffentlicht',
        },
        fields: {
            title: 'Titel',
            summary: 'Zusammenfassung',
            description: 'Beschreibung',
            name: 'Name',
            email: 'E-Mail',
            company: 'Firma',
            jobTitle: 'Position',
            user: 'Konto',
            userHelp: 'Ist die Person verknüpft, werden Name und E-Mail-Adresse des Kontos angezeigt.',
            noUser: 'Nicht verknüpft',
            members: 'Team',
            employeesOf: 'Personen von {{client}}',
            isParticipant: 'Hat teilgenommen',
            person: 'Person',
            noPerson: 'Niemand',
            supersedes: 'Ersetzt',
            noSupersedes: 'Nichts',
            supersedesHelp: {
                todo: 'Ein offenes To-do eines früheren Recaps, das dieses aufgreift. Sobald der Recap veröffentlicht ist, verschwindet es aus allen Listen.',
                event: 'Ein Termin eines früheren Recaps, den dieser verschiebt. Sobald der Recap veröffentlicht ist, verschwindet er aus allen Listen und Kalendern.',
            },
            dueDate: 'Fällig am',
            date: 'Datum',
            time: 'Uhrzeit',
            timeHelp: 'Leer lassen für einen ganztägigen Termin',
            topic: 'Thema',
        },
        edit: {
            add: 'Hinzufügen',
            addTopic: 'Thema',
            addPerson: 'Person',
            addFirstTopic: 'Thema hinzufügen',
            addFirstPerson: 'Person hinzufügen',
            addDecision: 'Entscheidung hinzufügen',
            addTodo: 'To-do hinzufügen',
            addEvent: 'Termin hinzufügen',
            noTopics: 'Noch keine Themen',
            noPersons: 'Noch keine Personen',
            moveUp: 'Nach oben',
            moveDown: 'Nach unten',
            markDone: 'Als erledigt markieren',
            markNotDone: 'Als offen markieren',
            titles: {
                recap: 'Recap bearbeiten',
                person: 'Person bearbeiten',
                topic: 'Thema bearbeiten',
                decision: 'Entscheidung bearbeiten',
                todo: 'To-do bearbeiten',
                event: 'Termin bearbeiten',
            },
            newTitles: {
                person: 'Neue Person',
                topic: 'Neues Thema',
                decision: 'Neue Entscheidung',
                todo: 'Neues To-do',
                event: 'Neuer Termin',
            },
            deleteTitles: {
                person: 'Person entfernen?',
                topic: 'Thema löschen?',
                decision: 'Entscheidung löschen?',
                todo: 'To-do löschen?',
                event: 'Termin löschen?',
            },
            deleteDescriptions: {
                person: '„{{name}}“ wird aus dem Recap entfernt. Die To-dos der Person bleiben erhalten, aber ohne Zuständigkeit.',
                topic: '„{{name}}“ wird mit allen Entscheidungen, To-dos und Terminen darin gelöscht.',
                decision: '„{{name}}“ wird gelöscht.',
                todo: '„{{name}}“ wird gelöscht.',
                event: '„{{name}}“ wird gelöscht.',
            },
        },
        publish: {
            button: 'Veröffentlichen',
            title: 'Version {{number}} veröffentlichen?',
            descriptionFirst:
                'Der Recap wird zu Version 1. Was du danach änderst, landet in einem Entwurf der nächsten Version.',
            descriptionNext: 'Der Entwurf wird zur nächsten Version und ersetzt die veröffentlichte.',
            changesSince: 'Geändert seit Version {{number}}',
            requireAcknowledgement: 'Erneut um Bestätigung bitten',
            submit: 'Veröffentlichen',
        },
        discard: {
            button: 'Entwurf verwerfen',
            title: 'Entwurf verwerfen?',
            description:
                'Alle Änderungen seit Version {{number}} gehen verloren, und der Recap ist wieder so, wie er veröffentlicht wurde. Abgehakte To-dos bleiben erledigt.',
            submit: 'Verwerfen',
        },
        restore: {
            button: 'Diese Version wiederherstellen',
            title: 'Version {{number}} wiederherstellen?',
            makesDraft:
                'Ihr Inhalt wird zum Entwurf von Version {{next}}, den du bearbeiten und veröffentlichen kannst.',
            replacesDraft:
                'Ihr Inhalt ersetzt den Entwurf von Version {{next}}. Alle Änderungen darin gehen verloren. Abgehakte To-dos bleiben erledigt.',
        },
        versions: {
            button: 'Versionen',
            title: 'Versionen',
            number: 'Version {{number}}',
            acknowledgementRequired: 'Bestätigung angefordert',
            view: 'Ansehen',
            first: 'Die erste veröffentlichte Version.',
            viewingTitle: 'Du siehst Version {{number}}',
            viewing: 'Veröffentlicht am {{date}}.',
            viewDraft: 'Entwurf ansehen',
            viewCurrent: 'Aktuelle Version ansehen',
            viewLatest: 'Neueste Version ansehen',
            viewPublished: 'Veröffentlichte Version ansehen',
        },
        sourceFile: {
            attachmentOf: 'Anhang von „{{subject}}“',
            attachment: 'Anhang einer E-Mail',
            from: 'Von',
            to: 'An',
            cc: 'Cc',
            more: '+{{count}}',
        },
        editSources: {
            button: 'Bearbeiten',
            edit: {
                title: 'Quellen bearbeiten',
                submit: 'Speichern und neu entwerfen',
            },
            process: {
                title: 'Neu verarbeiten',
                submit: 'Neu verarbeiten',
            },
            description: 'Angehakt ist, woraus der Recap neu entsteht.',
            new: 'Neu',
            createdAt: 'Recap erstellt am {{date}}',
            noSources: 'Keine Quelle wartet auf einen Recap.',
            keptTitle: 'Deine Änderungen bleiben',
            kept: 'Der neue Entwurf baut auf dem jetzigen Stand auf: Was von Hand geändert wurde, bleibt, und was die Quellen neu sagen, kommt dazu. Für einen ganz neuen Entwurf kannst du den Recap löschen und neu aus den Quellen erstellen.',
            nextVersion: 'Es entsteht ein Entwurf der nächsten Version.',
            empty: {
                title: 'Noch keine Quellen',
                description: 'Füge welche hinzu, um den Recap aus ihnen zu entwerfen.',
            },
        },
        changes: {
            none: 'Nichts geändert.',
            empty: 'leer',
            fields: {
                title: 'Titel',
                summary: 'Zusammenfassung',
                description: 'Beschreibung',
                name: 'Name',
                email: 'E-Mail',
                company: 'Firma',
                job_title: 'Position',
                is_participant: 'Hat teilgenommen',
                user: 'Konto',
                topic: 'Thema',
                person: 'Person',
                due_date: 'Fällig am',
                date: 'Datum',
                time: 'Uhrzeit',
            },
        },
        filters: {
            search: 'Suchen',
            status: 'Status',
            allStatuses: 'Alle Status',
            client: 'Kunde',
            allClients: 'Alle Kunden',
            withoutClient: 'Ohne Kunde',
            withoutProject: 'Ohne Projekt',
            project: 'Projekt',
            allProjects: 'Alle Projekte',
            publication: 'Veröffentlichung',
            allPublications: 'Alle',
            publications: {
                draft: 'Entwürfe',
                published: 'Veröffentlicht',
            },
            acknowledgement: 'Bestätigung',
            allAcknowledgements: 'Alle',
            acknowledgements: {
                waiting: 'Wartet auf Bestätigung',
                acknowledged: 'Bestätigt',
            },
        },
        acknowledgers: {
            title: 'Bestätigungen',
            who: 'Wer soll bestätigen?',
            withoutClient: 'Der Recap hat keinen Kunden, deshalb kann niemand um Bestätigung gebeten werden.',
            noEmployees: '{{client}} hat noch keine Personen.',
            noReaders: 'Noch liest niemand von {{client}} dieses Projekt mit.',
            chooseReaders: 'Auswählen, wer mitliest',
            none: 'Niemand wurde gebeten, diese Version zu bestätigen',
            acknowledged: 'Version {{number}} bestätigt · {{date}}',
            waiting: 'Ausstehend',
            waitingSince: 'Version {{acknowledged}} bestätigt, Version {{number}} ausstehend',
            editTitle: 'Wer soll bestätigen?',
            editDescription:
                'Gilt für die veröffentlichte Version {{number}}, ohne eine neue Version zu erstellen. Bisherige Bestätigungen bleiben erhalten.',
            count: '{{acknowledged}} von {{count}} bestätigt',
            all: 'Bestätigt',
            asked: 'Um Bestätigung gebeten: {{names}}',
            acknowledgedBy: 'Bestätigt von {{names}}',
            notAccepted: 'Hat die Einladung noch nicht angenommen',
            invitedHint:
                'Eingeladene Personen bekommen eine E-Mail und können bestätigen, sobald sie die Einladung angenommen haben.',
        },
        delete: {
            button: 'Löschen',
            title: 'Recap löschen?',
            confirm:
                '„{{name}}“ wird gelöscht. Seine Quellen bleiben erhalten. Das lässt sich nicht rückgängig machen.',
            submit: 'Löschen',
            blocked: 'Dieser Recap kann nicht gelöscht werden.',
        },
    },
    sources: {
        isNew: 'Neu seit deinem letzten Besuch',
        uploadDialog: {
            title_one: 'Datei hochladen',
            title_other: '{{count}} Dateien hochladen',
            description:
                'Wähle den Kunden und das Projekt, zu denen die Quellen gehören. Du kannst das auch später noch ändern.',
            submit: 'Hochladen',
        },
        pickDialog: {
            title_one: 'Eine Datei aus {{provider}}',
            title_other: '{{count}} Dateien aus {{provider}}',
            emailTitle_one: 'Eine E-Mail aus {{provider}}',
            emailTitle_other: '{{count}} E-Mails aus {{provider}}',
            documentTitle_one: 'Eine Seite aus {{provider}}',
            documentTitle_other: '{{count}} Seiten aus {{provider}}',
            taskTitle_one: 'Eine Karte aus {{provider}}',
            taskTitle_other: '{{count}} Karten aus {{provider}}',
            submit: 'Hinzufügen',
            reading: 'Wird gelesen …',
            failedTitle: 'Die Dateien konnten nicht hinzugefügt werden',
        },
        pickItems: {
            title: {
                email: 'E-Mails auswählen',
                document: 'Dokumente auswählen',
                task: 'Karten auswählen',
            },
            place: {
                email: 'Ordner',
                document: 'Bereich',
                task: 'Board oder Liste',
            },
            search: {
                email: 'E-Mails durchsuchen',
                document: 'Dokumente durchsuchen',
                task: 'Karten durchsuchen',
            },
            shown: 'Welche',
            new: 'Neue',
            all: 'Alle',
            next: 'Weiter',
            limit: 'Höchstens {{count}} auf einmal',
            added: '{{title}} (schon hinzugefügt)',
            attachments: 'Mit Anhängen',
            noSubject: '(Ohne Betreff)',
            due: 'fällig {{date}}',
            allAdded: 'Was hier liegt, ist schon hinzugefügt.',
            showAll: 'Alle zeigen',
            loading: 'Wird geladen …',
            empty: 'Hier liegt nichts.',
            noResults: 'Nichts gefunden zu „{{search}}“.',
            clearSearch: 'Suche zurücksetzen',
        },
        textDialog: {
            title: 'Quelle aus Text hinzufügen',
            description:
                'Füge Notizen, eine Nachricht oder ein Transkript ein. Wähle den Kunden und das Projekt, zu denen der Text gehört – du kannst das auch später noch ändern.',
            titleLabel: 'Titel',
            defaultTitle: 'Notiz',
            text: 'Text',
            submit: 'Hinzufügen',
        },
        linkDialog: {
            title: 'Quelle aus Link hinzufügen',
            description:
                'Recap liest den Link und macht daraus eine Quelle. Wähle den Kunden und das Projekt, zu denen sie gehört – du kannst das auch später noch ändern.',
            url: 'Link',
            shareInGoogleDrive: 'In Google Drive freigeben',
            notShared:
                'Recap hat noch keinen Zugriff auf diese Datei. Gib sie in {{name}} frei, damit sie zur Quelle werden kann.',
            readThrough: 'Wird über {{name}} gelesen',
            notConnected:
                'Es gibt noch keine Verbindung zu {{name}}. Verbinde den Dienst in den Einstellungen, um diesen Link zu lesen.',
            submit: 'Hinzufügen',
            reading: 'Wird gelesen …',
            existing: 'Diese Quelle gibt es schon.',
        },
        update: {
            title: 'In {{provider}} geändert',
            description:
                'Seit die Quelle eingegangen ist, wurde sie dort geändert. Übernimm die Aktualisierung, um sie neu einzulesen.',
            service: 'der Originalquelle',
            take: 'Aktualisierung übernehmen',
            pill: 'Aktualisierung verfügbar',
            confirmTitle: 'Aktualisierung übernehmen?',
            confirm: 'Die Quelle wird durch den aktuellen Stand ersetzt und neu eingelesen.',
            confirmWithRecaps_one:
                'Die Quelle wird durch den aktuellen Stand ersetzt und neu eingelesen. Danach wird der Recap {{recaps}} neu entworfen, und Änderungen von Hand in seinem Entwurf werden ersetzt. Veröffentlichte Versionen bleiben bestehen, bis die nächste veröffentlicht wird.',
            confirmWithRecaps_other:
                'Die Quelle wird durch den aktuellen Stand ersetzt und neu eingelesen. Danach werden die Recaps {{recaps}} neu entworfen, und Änderungen von Hand in ihren Entwürfen werden ersetzt. Veröffentlichte Versionen bleiben bestehen, bis die nächste veröffentlicht wird.',
        },
        via: 'über {{name}}',
        fromMailboxOf: 'aus dem Postfach von {{name}}',
        // How it came in without a connection.
        origin: {
            upload: 'per Upload',
            email: 'per E-Mail',
            mobile: 'per Smartphone',
            document: 'als Dokument',
            link: 'per Link',
            ticket: 'als Ticket',
            calendar_event: 'aus dem Kalender',
            meeting: 'aus einer Besprechung',
            conversation: 'aus einer Unterhaltung',
        },
        openIn: 'In {{name}} öffnen',
        resultCount_one: '{{count}} Quelle',
        resultCount_other: '{{count}} Quellen',
        emptyPage: {
            title: 'Noch keine Quellen',
            description:
                'Aus Quellen entstehen die Recaps. Lade Dateien hoch oder <connect>verbinde ein Postfach</connect>, dann kommen E-Mails von selbst an.',
        },
        noResults: {
            title: 'Keine Quellen gefunden',
            description: 'Keine Quelle passt zu deiner Suche und deinen Filtern.',
            descriptionSearch: 'Keine Quelle passt zu deiner Suche.',
            descriptionFilters: 'Keine Quelle passt zu deinen Filtern.',
            searchEverywhere: '„{{search}}“ in allen Quellen suchen',
            showFiltered_one: 'Alle Quellen mit diesem Filter zeigen',
            showFiltered_other: 'Alle Quellen mit diesen Filtern zeigen',
            showAll: 'Alle Quellen zeigen',
        },
        processingTitle: 'Wird verarbeitet',
        unfiledTitle: 'Noch in keinem Recap',
        untitled: 'Ohne Titel',
        failedTitle: 'Die Verarbeitung ist fehlgeschlagen',
        failed: 'Du kannst sie unter „Weitere Aktionen“ neu starten.',
        processAgain: 'Neu verarbeiten',
        fileCount_one: '{{count}} Datei',
        fileCount_other: '{{count}} Dateien',
        sections: {
            email: 'E-Mail',
            content: 'Extrahierter Text',
            recaps: 'Recaps',
        },
        content: {
            hint: 'Der Text, der aus den Dateien gelesen wurde und aus dem die Recaps entstehen.',
            empty: 'Kein Text extrahiert',
            processing: 'Wird gelesen …',
        },
        recapsEmpty: {
            description: 'Aus dieser Quelle entsteht ein Recap, allein oder zusammen mit anderen Quellen.',
        },
        syncErrorTitle: 'Eine Verbindung konnte zuletzt nicht abgerufen werden',
        syncError: 'Details findest du in den Einstellungen.',
        signedOutTitle_one: 'Eine Verbindung muss neu verbunden werden',
        signedOutTitle_other: '{{count}} Verbindungen müssen neu verbunden werden',
        signedOut: 'Bis dahin kommt über {{connections}} nichts herein.',
        select: '{{name}} auswählen',
        selectMode: 'Auswählen',
        add: {
            button: 'Hinzufügen',
            upload: 'Dateien hochladen',
            photo: 'Foto aufnehmen',
            link: 'Aus Link',
            text: 'Text einfügen',
            pickFrom: 'Auswählen aus',
            googleDrive: 'Google Drive',
            email: 'Per E-Mail',
            teamsTranscript: 'Teams-Transkript hochladen',
            teamsTranscriptDescription:
                'Ein Transkript, das du in Teams heruntergeladen hast, wird zur Quelle mit allen Sprechenden. Auch Zooms .vtt-Transkripte gehen.',
            chooseFile: 'Datei auswählen',
            emailTitle: 'Per E-Mail',
            emailDescription:
                'Verbinde ein Postfach (IMAP). „Abrufen“ holt die neuen E-Mails, und jede wird zu einer Quelle. Den Kunden ordnet Recap nach den Regeln zu, die du für das Postfach festlegst.',
            emailDescriptionWithAddress:
                'Schick oder leite E-Mails an die Adresse des Workspace weiter, und jede wird zu einer Quelle. Kunden und Projekte haben eigene Adressen in ihren Einstellungen: Was dorthin geht, landet gleich beim Kunden oder Projekt. Oder verbinde ein Postfach (IMAP), dessen E-Mails Recap nach deinen Regeln zuordnet.',
            connectedMailboxes: 'Verbunden: {{mailboxes}}',
            connectMailbox: 'Postfach verbinden',
        },
        upload: {
            button: 'Dateien hochladen',
            drop: 'Dateien hier ablegen',
            uploading: 'Wird hochgeladen …',
            failed: 'Hochladen fehlgeschlagen',
            rejectedTitle: 'Einige Dateien wurden nicht hochgeladen',
            rejectedType:
                'Unterstützt werden nur PDFs, Bilder, E-Mails, Aufnahmen, Word, Excel, PowerPoint und Textdateien: {{files}}',
            rejectedSize: 'Größer als {{size}} MB (Aufnahmen: {{audioSize}} MB): {{files}}',
        },
        filters: {
            search: 'Suchen',
            status: 'Status',
            allStatuses: 'Alle Status',
            client: 'Kunde',
            allClients: 'Alle Kunden',
            withoutClient: 'Ohne Kunde',
            recap: 'Recap',
            allRecaps: 'Alle',
            withoutRecap: 'In keinem Recap',
            inRecap: 'In einem Recap',
        },
        project: {
            assign: 'Projekt zuordnen',
            change: 'Projekt ändern',
        },
        client: {
            button: 'Kunde und Projekt',
            change: 'Kunde und Projekt ändern',
            title_one: 'Kunde und Projekt',
            title_other: 'Kunde und Projekt zuordnen',
            description_one: 'Zu welchem Kunden und Projekt gehört diese Quelle?',
            description_other: 'Zu welchem Kunden und Projekt gehören die {{count}} ausgewählten Quellen?',
        },
        files: {
            emailBody: 'Text der E-Mail',
            pageCount_one: '{{count}} Seite',
            pageCount_other: '{{count}} Seiten',
            image: 'Bild',
            recording: 'Aufnahme',
            download: '{{name}} herunterladen',
            downloadButton: 'Herunterladen',
        },
        email: {
            from: 'Von',
            to: 'An',
            cc: 'Cc',
            sent: 'Gesendet',
        },
        delete: {
            button: 'Löschen',
            title: 'Quelle löschen?',
            confirm:
                '„{{name}}“ wird mit allen Dateien gelöscht. Recaps, die daraus entstanden sind, bleiben mit ihren Fundstellen erhalten. Das lässt sich nicht rückgängig machen.',
            submit: 'Löschen',
            blocked: 'Diese Quelle kann nicht gelöscht werden.',
        },
    },
    emailAddresses: {
        title: 'E-Mail-Adresse',
        address: 'Private Adresse',
        renew: 'Neue Adresse erstellen',
        renewTitle: 'Neue Adresse erstellen?',
        renewConfirm:
            '{{address}} nimmt danach keine E-Mails mehr an. Trage die neue Adresse überall ein, wo du die bisherige verwendet hast.',
        anyone: 'Diese Adresse nimmt E-Mails von allen an, die sie kennen. Gib sie deshalb nur an Personen und Tools weiter, denen du vertraust, und erstelle eine neue Adresse, wenn sie in falsche Hände gerät.',
        forwarding:
            'Richtest du in Gmail eine automatische Weiterleitung von der Adresse ein, mit der du bei Recap angemeldet bist, schicken wir dir Googles Bestätigungslink per E-Mail.',
        prefix: {
            title: 'Präfix ändern',
            description:
                'Wohin eine E-Mail geht, entscheidet nur der Teil nach dem Unterstrich. Das Präfix davor ist nur für dich, damit du die Adresse wiedererkennst, zum Beispiel am Namen des Tools, das an sie schickt. Mit dem bisherigen Präfix kommen E-Mails weiter an.',
            label: 'Präfix',
            hint: 'Ohne Präfix beginnt die Adresse mit dem Kürzel. Die Adresse lautet: {{address}}',
            submit: 'Ändern',
        },
        workspace: {
            hint: 'Jede E-Mail an diese Adresse wird eine Quelle. Kommt sie von einer Person eines Kunden, gehört die Quelle gleich zu ihrem Kunden.',
        },
        client: {
            hint: 'Jede E-Mail an diese Adresse wird eine Quelle von „{{client}}“.',
        },
        project: {
            hint: 'Jede E-Mail an diese Adresse wird eine Quelle von „{{project}}“.',
        },
        employee: {
            client: 'Unterlagen per E-Mail',
            clientHint:
                'Schick oder leite E-Mails an diese Adresse weiter, damit sie beim Team von „{{workspace}}“ ankommen.',
            projectHint: 'E-Mails an diese Adresse kommen beim Team als Unterlagen zu „{{project}}“ an.',
        },
    },
    support: {
        title: 'Problem melden',
        description: 'Beschreibe, was passiert ist und was du stattdessen erwartet hast. Wir antworten dir per E-Mail.',
        whatHappened: 'Was ist passiert?',
        sentAlong: 'Wird mitgeschickt',
        name: 'Name',
        email: 'E-Mail',
        workspace: 'Workspace',
        workspaceWithRole: '{{workspace}} · {{role}}',
        roles: {
            owner: 'Inhaber:in',
            member: 'Mitglied',
            employee: 'Person eines Kunden',
        },
        page: 'Seite',
        browser: 'Browser',
        browserOnSystem: '{{browser}} auf {{system}}',
        errorLog: 'Fehlerprotokoll',
        errorCount_one: '{{count}} Fehler',
        errorCount_other: '{{count}} Fehler',
        submit: 'Problem melden',
        sentTitle: 'Danke für deine Meldung',
        sent: 'Deine Meldung ist angekommen. Wir melden uns per E-Mail bei dir.',
    },
    errorPage: {
        errorEyebrow: 'Fehler',
        notFoundEyebrow: '404',
        errorText: 'Diese Seite ließ sich nicht laden. Lade sie neu – wenn der Fehler bleibt, melde uns das Problem.',
        errorTextSignedOut: 'Diese Seite ließ sich nicht laden. Lade sie neu oder versuch es später noch einmal.',
        notFoundText:
            'Unter dieser Adresse gibt es keine Seite. Vielleicht wurde sie gelöscht, oder der Link ist unvollständig.',
        reload: 'Neu laden',
        errorLogSentAlong: 'Beim Melden geht das Fehlerprotokoll mit.',
    },
    enums: {
        extractionStatus: {
            processing: 'Wird verarbeitet',
            done: 'Fertig',
            failed: 'Fehlgeschlagen',
        },
        sourceKind: {
            upload: 'Hochgeladen',
            email: 'E-Mail',
            mobile: 'Smartphone',
            document: 'Dokument',
            link: 'Link',
            ticket: 'Ticket',
            calendar_event: 'Termin',
            meeting: 'Besprechung',
            conversation: 'Unterhaltung',
        },
    },
};

export type Translations = typeof de;

export default de;
